import * as React from 'react';
import styled from 'styled-components';
import { PDFExport } from '@progress/kendo-react-pdf';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import Workflow from '../../models/be/Workflow';
import { useAsyncAction } from '../../commonComponents/Hooks';
import Loading from '../../commonComponents/Loading';
import MessageBox from '../common/MessageBox';
import { euCountries, centralAmericanCountries, southAmericanCountries } from '../../core/constants';
import { getCountryName } from '../../core/countryList';
import { hasValue, getCustomerById } from '../../lib/utils';
import Portal from '../../commonComponents/Portal';
import { PdfWrapper } from '../common/StyledComponents';
import { getFile, getWorkflow } from '../../core/api';
import { getCustomerUser } from '../../core/coreApi';
import { getCustomerUserIdFromAccessToken } from '../../core/session';
import User from '../../models/be/coreApi/User';
import { getFullNameString } from '../../models/be/FullName';
import PrimaryName from '../../models/be/PrimaryName';
import AddressHistory from '../../models/be/AddressHistory';
import RootState from '../../models/RootState';
import { customerContext } from '../../contexts/customer/CustomerContext';
import aisLogo from '../../images/AIS-USA-logo.png';

const safeFormat = (date: Date | string | null | undefined, formatStr: string): string => {
	if (!date) return '';
	const d = new Date(date);
	return Number.isNaN(d.getTime()) ? '' : format(d, formatStr);
};

const ConsentDocumentAIS = (props: Props) => {
	const [loading, errorMessage, performAsyncAction] = useAsyncAction(true);
	const context = React.useContext(customerContext);
	const { workflowId } = props;
	const customerDetails = useSelector((state: RootState) => state?.customerDetails);

	const [workflow, setWorkflow] = React.useState<Workflow>();
	const [pdfSaved, setPdfSaved] = React.useState(false);
	const [workflowImage, setWorkflowImage] = React.useState<string>(null);
	const [currentUser, setCurrentUser] = React.useState<User>();
	const [ownerUser, setOwnerUser] = React.useState<User>();

	React.useEffect(() => {
		performAsyncAction(async () => {
			const newWorkflow = await getWorkflow(workflowId);
			if (newWorkflow?.consent?.signature) {
				const fileName = newWorkflow.consent?.signature;
				const image = await getFile(fileName);

				if (image) {
					setWorkflowImage(`data:image/png;base64,${image}`);
				}
			}
			setWorkflow(newWorkflow);

			try {
				const currentUserId = getCustomerUserIdFromAccessToken();
				if (hasValue(currentUserId)) {
					setCurrentUser(await getCustomerUser(currentUserId));
				}
			} catch {
				// Best-effort lookup; leave currentUser undefined if it fails.
			}

			if (hasValue(newWorkflow?.customerUserId)) {
				try {
					setOwnerUser(await getCustomerUser(newWorkflow.customerUserId));
				} catch {
					// Best-effort lookup; leave ownerUser undefined if it fails.
				}
			}
		});
	}, []);

	const customerName = () =>
		hasValue(customerDetails)
			? customerDetails.customerName
			: getCustomerById(context.customer.value, workflow?.clientCustomerId)?.customerName;

	if (loading) return <Loading />;

	if (errorMessage) return <MessageBox error={errorMessage} />;

	if (props.exportAsPdf) {
		const handlePdfExportRef = (component: PDFExport) => {
			if (!pdfSaved && hasValue(component)) {
				component.save(props.onSave);
				setPdfSaved(true);
			}
		};

		return (
			<Portal>
				<PdfWrapper>
					<PDFExport ref={handlePdfExportRef} fileName="ConsentDocumentAIS.pdf" paperSize="A4" scale={0.8}>
						<ConsentDocumentAISView
							image={workflowImage}
							workflow={workflow}
							customerName={customerName()}
							candidatePhone={workflow?.mobile}
							currentUser={currentUser}
							ownerUser={ownerUser}
						/>
					</PDFExport>
				</PdfWrapper>
			</Portal>
		);
	}
	return (
		<ConsentDocumentAISView
			image={workflowImage}
			workflow={workflow}
			customerName={customerName()}
			candidatePhone={workflow?.mobile}
			currentUser={currentUser}
			ownerUser={ownerUser}
		/>
	);
};

export default ConsentDocumentAIS;

interface Props {
	workflowId: string;
	exportAsPdf?: boolean;
	onSave?: () => void;
}

ConsentDocumentAIS.defaultProps = {
	exportAsPdf: false,
	onSave: null
};

const isEuCountry = (countryCode: string): boolean => {
	return euCountries.includes(countryCode);
};

// Helper functions for country-specific consents
const isCentralAmericanCountry = (countryCode: string): boolean => {
	return centralAmericanCountries.includes(countryCode);
};

const isSouthAmericanCountry = (countryCode: string): boolean => {
	return southAmericanCountries.includes(countryCode);
};

const getCurrentAddress = (addressHistories: AddressHistory[]): string => {
	if (!addressHistories || addressHistories.length === 0) {
		return '';
	}

	const currentAddress =
		addressHistories.find((addr) => !addr.toDate) ||
		addressHistories.reduce((latest, current) => {
			if (!latest.toDate && !current.toDate) return latest;
			if (!latest.toDate) return latest;
			if (!current.toDate) return current;
			return new Date(current.toDate) > new Date(latest.toDate) ? current : latest;
		});

	if (!currentAddress) {
		return '';
	}

	const addressParts = [
		currentAddress.unitNumber,
		currentAddress.buildingName,
		currentAddress.streetNumber,
		currentAddress.streetName,
		currentAddress.locality,
		currentAddress.suburb,
		currentAddress.state,
		currentAddress.postcode,
		currentAddress.country
	].filter((part) => part && part.trim() !== '');

	return addressParts.join(', ');
};
const getCurrentAddressHistory = (addressHistories: AddressHistory[]): AddressHistory | null => {
	if (!addressHistories || addressHistories.length === 0) return null;
	return (
		addressHistories.find((addr) => !addr.toDate) ||
		addressHistories.reduce((latest, current) => {
			if (!latest.toDate) return latest;
			if (!current.toDate) return current;
			return new Date(current.toDate) > new Date(latest.toDate) ? current : latest;
		})
	);
};

// EU-specific consent document content (Consent_EU_V2.4.pdf)
const euConsentContent = (workflow: Workflow, timeStamp: Date, image: string) => (
	<>
		<HeaderWrapper>
			<Header2>AUTHORIZATION</Header2>
		</HeaderWrapper>
		<ContentWrapper>
			<p>I, the undersigned</p>
			<br />
			<Name>
				<LeftCell>Given name: </LeftCell>
				<RightCell>{workflow.primaryName.firstName}</RightCell>
			</Name>
			<br />
			<Name>
				<LeftCell>Family name: </LeftCell>
				<RightCell>{workflow.primaryName.lastName}</RightCell>
			</Name>
			<br />
			<Name>
				<LeftCell>Family name at birth: </LeftCell>
				<RightCell>{workflow.primaryName.lastName}</RightCell>
			</Name>
			<br />
			<Name>
				<LeftCell>Nationality: </LeftCell>
				<RightCell>{workflow.birthDetails?.nationality}</RightCell>
			</Name>
			<br />
			<Name>
				<LeftCell>Date of birth: </LeftCell>
				<RightCell>{workflow.birthDetails?.birthDate}</RightCell>
			</Name>
			<br />
			<Name>
				<LeftCell>Place and country of birth: </LeftCell>
				<RightCell>{workflow.birthDetails?.country}</RightCell>
			</Name>
			<br />
			<Name>
				<LeftCell>Permanent residence: </LeftCell>
				<RightCell>{getCurrentAddress(workflow.addressHistories)}</RightCell>
			</Name>
			<br />
			<Name>
				<LeftCell>Are you an EU national: </LeftCell>
				<RightCell>yes</RightCell>
			</Name>
			<br />
			<Name>
				<LeftCell>If so, National ID Number: </LeftCell>
				<RightCell>{workflow.icrmDetails?.secondaryIdentificationNumber}</RightCell>
			</Name>
			<br />
			<p>hereby authorize</p>
			<p>
				AIS, LLC; AIS International Group, PTY LTD and its processors or sub processors; namely Scaut s.r.o., ID No. 092
				96 182, with registered office at Innova&#269;n&#237; 122, 252 41, Zlatn&#237;ky &#8211; Hodkovice, Czechia
				(hereinafter referred to as the &quot;Proxy&quot;) and its employees,
			</p>
			<p>to carry out the following on my behalf:</p>
			<p>
				- request and obtain criminal records extract from the national criminal register and ECRIS registry;
				<br />- collect all statements and certificates issued under this authorization.
			</p>
			<p>The Proxy is entitled to act independently.</p>
			<p>
				This Authorization is granted to the Proxy for a period of 45 days and may be revoked at any time by delivering
				a written notice of revocation to the Proxy.
			</p>
			<br />
			<Name>
				<LeftCell>Signature: </LeftCell>
				<RightCell>{image && <Image src={image} alt="signature" />}</RightCell>
			</Name>
			<br />
			<Name>
				<LeftCell>Date: </LeftCell>
				<RightCell>{safeFormat(timeStamp, 'dd/MM/yyyy')}</RightCell>
			</Name>
		</ContentWrapper>
	</>
);

// EU Authorization consent (used by Belgium, Finland) - (Consent_EU_V2.1.pdf)
const euAuthorizationConsentContent = (workflow: Workflow, timeStamp: Date, image: string) => (
	<>
		<HeaderWrapper>
			<Header2>AUTHORIZATION</Header2>
		</HeaderWrapper>
		<ContentWrapper>
			<p>I, the undersigned</p>
			<br />
			<Name>
				<LeftCell>Given name: </LeftCell>
				<RightCell>{workflow.primaryName.firstName}</RightCell>
			</Name>
			<br />
			<Name>
				<LeftCell>Family name: </LeftCell>
				<RightCell>{workflow.primaryName.lastName}</RightCell>
			</Name>
			<br />
			<Name>
				<LeftCell>Family name at birth: </LeftCell>
				<RightCell>{workflow.primaryName.lastName}</RightCell>
			</Name>
			<br />
			<Name>
				<LeftCell>Nationality: </LeftCell>
				<RightCell>{workflow.birthDetails?.nationality}</RightCell>
			</Name>
			<br />
			<Name>
				<LeftCell>Date of birth: </LeftCell>
				<RightCell>{workflow.birthDetails?.birthDate}</RightCell>
			</Name>
			<br />
			<Name>
				<LeftCell>Place and country of birth: </LeftCell>
				<RightCell>{workflow.birthDetails?.country}</RightCell>
			</Name>
			<br />
			<Name>
				<LeftCell>Permanent residence: </LeftCell>
				<RightCell>{getCurrentAddress(workflow.addressHistories)}</RightCell>
			</Name>
			<br />
			<p>hereby authorize</p>
			<p>
				Accurate Information Systems, LLC ; AIS International Group, PTY LTD and its processors or sub processors;
				namely Scaut s.r.o., ID No. 092 96 182, with registered office at Innova&#269;n&#237; 122, 252 41, Zlatn&#237;ky
				&#8211; Hodkovice, Czechia (hereinafter referred to as the &quot;Proxy&quot;) and its employees,
			</p>
			<p>to carry out the following on my behalf:</p>
			<p>
				- request and obtain criminal records extract from the national criminal register and ECRIS registry;
				<br />- collect all statements and certificates issued under this authorization.
			</p>
			<p>The Proxy is entitled to act independently.</p>
			<p>
				This Authorization is granted to the Proxy for a period of 45 days and may be revoked at any time by delivering
				a written notice of revocation to the Proxy.
			</p>
			<br />
			<Name>
				<LeftCell>Signature: </LeftCell>
				<RightCell>{image && <Image src={image} alt="signature" />}</RightCell>
			</Name>
			<br />
			<Name>
				<LeftCell>Date: </LeftCell>
				<RightCell>{safeFormat(timeStamp, 'dd/MM/yyyy')}</RightCell>
			</Name>
		</ContentWrapper>
	</>
);

// Finland-specific consent document content (Consent_EU_V2.1.pdf)
const aisInternationalConsentContent = (primaryName: PrimaryName, timeStamp: Date, image: string) => (
	<>
		<AisHeaderWrapper>
			<div>AIS International Group Level 13, 50 Cavill Ave, Surfers Paradise Qld 4217 Australia</div>
			<AisHeader3>AIS International Group General Declaration of Consent</AisHeader3>
		</AisHeaderWrapper>
		<AisContentWrapper>
			<p>
				In connection with this request I authorise AIS International Group and or its subsidiaries, sub processors, all
				governmental and private agencies within the North American Region, European Union, or any member state thereof,
				Asia Pacific (APAC); Middle East; South America or African regions, to release information they may have related
				to my criminal record, educational or employment record to the person or company with which this form has been
				filed, or their agent or sub processor, for that purpose in which this consent was obtained. I authorise this
				consent to any nation or my home country of residence.
			</p>
			<p>
				I authorise AIS Intl or its assigned sub-processor(s) or subsidiaries acting as an independent agent on my
				behalf to verify information presented on my submitted form and in any submitted and/or attached documents or
				identification. I understand that these documents may include sensitive personal data for the purposes of the UK
				Data Protection legislation, and the obtaining of documents and/or information which is covered by the European
				legislation (General Data Protection Regulation – GDPR) and/or by applicable data protection legislation which
				is relevant in any other country. I also understand and agree that, where my personal history requires
				background verifications to be carried out in countries where I have worked and/or resided, in respect of my
				criminality, employment or education or residence, my data may be transmitted, in any required delivery method
				(Electronic, facsimile, mail, currier, etc.) to and processed in those relevant countries in any required
				format,
				<BoldText>
					including those countries that do not have sufficient data protection standards as stated by the EEA
					authorities.
				</BoldText>
				I understand and agree that the data may also be transmitted back in any similar transmitted form to the origin
				of the request. I further authorise AIS International group to forward, in any format, the findings in report
				format to the company/agency for which this form was authorised. I understand and agree that the information
				provided on this form and that contained in the attached documents, if required, is true and complete to the
				best of my knowledge.
			</p>
			<p>
				My signature below acknowledges the fact that I have read, understand, and freely give my unambiguous consent to
				the handling of my personal information. I agree that AIS International Group or assigned sub-processors may
				contact the custodian(s) of record either by phone, in person, email or regular mail for the purposes set out
				above.
			</p>
			<p>
				Furthermore, I hereby release the aforesaid parties or the Company or individuals that release information about
				me from any liability whatsoever in collecting and disseminating the information obtained. I confirm that I have
				read and understood the AIS International Group Privacy Policy, and I am aware of my subject rights under GDPR
				or any other concerned data protection laws or regulations.
			</p>
			<p>
				<a href="https://aisintl.com.au/privacy-policy/">https://aisintl.com.au/privacy-policy/</a>
				&nbsp;&nbsp;&nbsp;
				<a href="https://aisintl.com.au/terms-and-conditions/">https://aisintl.com.au/terms-and-conditions/</a>
			</p>

			<AisHeader4>The following information will be checked as requested:</AisHeader4>
			<p>
				<strong>*Criminality:</strong> By the relevant jurisdictional agency containing the details of criminal
				convictions considered unspent under jurisdictional spent conviction laws.
				<br />
				<strong>*Financial Verification:</strong> Via the relevant credit bureau in any given country.
				<br />
				<strong>*Qualification Verification:</strong> Via the relevant University in any given country.
				<br />
				<strong>*Employment Verification:</strong> Via the relevant employer of any given applicant
				<br />
				<strong>*Social Media:</strong> Via the applicants given social media links.
			</p>

			<p>
				<GreenText>ID Authority:</GreenText>I authorise AIS International Group,{' '}
				<UnderlineText>where applicable</UnderlineText> to <BoldText>verify my ID</BoldText>. I confirm that I am
				authorised to provide the personal details presented and I consent to my information being checked with the
				document issuer or official record holder via third party systems for the purpose of confirming my identity.
			</p>

			<SignatureSection>
				<SignatureTable>
					<tbody>
						<tr>
							<td>
								<SignatureField>
									{primaryName.firstName} {primaryName.middleNames} {primaryName.lastName}
								</SignatureField>
								<SignatureLabel>Applicant Name (please print)</SignatureLabel>
							</td>
							<td style={{ paddingLeft: '40px' }}>
								<SignatureField>{safeFormat(timeStamp, 'dd/MM/yyyy')}</SignatureField>
								<SignatureLabel>Date (day/month/year)</SignatureLabel>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<SignatureField>{image && <Image src={image} alt="signature" />}</SignatureField>
								<SignatureLabel>Applicant Signature</SignatureLabel>
							</td>
						</tr>
					</tbody>
				</SignatureTable>
			</SignatureSection>

			<RedFooterNote>
				*Please be advised that your Personal Information / Data will be deleted from our system automatically after 12
				months from your application release date. We recommend that you save a copy of your criminal history report for
				your records.
			</RedFooterNote>

			<ABNContainer>
				<ABNFooter>ABN 96 608 458 939</ABNFooter>
			</ABNContainer>
		</AisContentWrapper>

		<AisHeaderWrapper style={{ marginTop: '80px' }}>
			<div>AIS International Group Level 13, 50 Cavill Ave, Surfers Paradise Qld 4217 Australia</div>
			<BankingHeader>Banking Authority for Credit Card, Debit Card & PayPal</BankingHeader>
		</AisHeaderWrapper>

		<AisContentWrapper2>
			<RedText>Please complete this page only if you are an individual paying for your own criminal check.</RedText>

			<p>
				<GreenText>Bank Authority:</GreenText> I authorise AIS International Group,{' '}
				<UnderlineText>where applicable</UnderlineText>, by my signature below, to charge my credit card, debit card or
				PayPal account, that was used to request an ICHC (International criminal history check) on my behalf, for the
				<BoldText> total cost</BoldText> (including GST) of the checks I have requested.
			</p>

			<p>
				I agree and acknowledge that the charges cannot be refunded after 24 hours of submission of my ICHC application
				unless extenuating circumstances occur.
			</p>

			<SignatureSection>
				<SignatureTable>
					<tbody>
						<tr>
							<td>
								<SignatureField>
									{primaryName.firstName} {primaryName.middleNames} {primaryName.lastName}
								</SignatureField>
								<SignatureLabel>Applicant Name (please print)</SignatureLabel>
							</td>
							<td style={{ paddingLeft: '40px' }}>
								<SignatureField>{safeFormat(timeStamp, 'dd/MM/yyyy')}</SignatureField>
								<SignatureLabel>Date (day/month/year)</SignatureLabel>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<SignatureField>{image && <Image src={image} alt="signature" />}</SignatureField>
								<SignatureLabel>Applicant Signature</SignatureLabel>
							</td>
						</tr>
					</tbody>
				</SignatureTable>
			</SignatureSection>

			<RedFooterNote>
				*Please be advised that your Personal Information / Data will be deleted from our system automatically after 12
				months from your application release date. We recommend that you save a copy of your criminal history report for
				your records.
			</RedFooterNote>
			<ABNContainer1>
				<ABNFooter>ABN 96 608 458 939</ABNFooter>
			</ABNContainer1>
		</AisContentWrapper2>
	</>
);

// Brazil consent content (Portuguese)
// NOTE: Country-specific AIS consents now use jsPDF which properly handles UTF-8 characters
const brazilConsentContent = (
	primaryName: PrimaryName,
	timeStamp: Date,
	image: string,
	workflow: Workflow,
	customerName: string
) => (
	<>
		<AisHeaderWrapper style={{ textAlign: 'left' }}>
			<BrazilHeader>CONSENTIMENTO PARA COMPARTILHAR INFORMAÇÃO PESSOAL</BrazilHeader>
			<div style={{ fontSize: '12px', marginTop: '10px', fontFamily: 'Arial, Helvetica, sans-serif' }}>
				Autorização para realizar investigação de antecedentes criminais
			</div>
		</AisHeaderWrapper>
		<AisContentWrapper style={{ marginLeft: '10px', marginRight: '10px' }}>
			<p>
				Certifico que a informação apresentada pela minha pessoa nesta solicitação é verdadeira e correta. Concordo que
				seja realizada a investigação criminal de <strong>{customerName || '__________________________'}</strong> E seus
				associados. Ao assinar esta cópia estou consciente disto e dou minha permissão para que este histórico seja
				efetuado de maneira eletrônica e seja arquivado em cópia física para propósitos de trabalho unicamente.
			</p>
			<p>
				Pela presente, permito sua realização e libero de qualquer responsabilidade todo empregado dos escritórios da
				Autoridade Competente que seja responsabilizado por qualquer consequência que possa resultar ao autorizar o
				fornecimento da minha informação por parte da Autoridade competente com <strong>VETTING.com</strong>.
			</p>

			<SignatureSection>
				<SignatureTable>
					<tbody>
						<tr>
							<td colSpan={2}>
								<SignatureLabel>Assinatura do Solicitante:</SignatureLabel>
								<SignatureField>{image && <Image src={image} alt="signature" />}</SignatureField>
							</td>
						</tr>
						<tr>
							<td>
								<SignatureLabel>Data:</SignatureLabel>
								<SignatureField>{safeFormat(timeStamp, 'dd/MM/yyyy')}</SignatureField>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<SignatureLabel>Nome:</SignatureLabel>
								<SignatureField>
									{primaryName.lastName} / {primaryName.firstName} {primaryName.middleNames || ''}
								</SignatureField>
								<div style={{ fontSize: '10px', marginTop: '5px' }}>(Sobrenome do Pai / Sobrenome da Mãe / Nomes)</div>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<SignatureLabel>
									<strong>CPF nombre</strong> (Cadastro de Pessoas Fisicas):
								</SignatureLabel>
								<SignatureField>
									{workflow.icrmDetails?.secondaryIdentificationNumber || '_______________________'}
								</SignatureField>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<SignatureLabel>
									<strong>Registro Geral</strong> (RG) number:
								</SignatureLabel>
								<SignatureField>_______________________</SignatureField>
							</td>
						</tr>
					</tbody>
				</SignatureTable>
			</SignatureSection>
		</AisContentWrapper>
	</>
);

// Canada consent content - Page 1: Informed Consent Form
const getCurrentCityProvince = (addressHistories: AddressHistory[]): string => {
	if (!addressHistories || addressHistories.length === 0) return '';
	const current =
		addressHistories.find((addr) => !addr.toDate) ||
		addressHistories.reduce((latest, cur) => {
			if (!cur.toDate) return cur;
			if (!latest.toDate) return latest;
			return new Date(cur.toDate) > new Date(latest.toDate) ? cur : latest;
		});
	if (!current) return '';
	return [current.suburb, current.state].filter(Boolean).join(', ');
};

const formatAddressLine = (address: AddressHistory): string => {
	const parts = [
		address.unitNumber,
		address.buildingName,
		address.streetNumber,
		address.streetName,
		address.locality,
		address.suburb,
		address.state,
		address.postcode,
		address.country
	].filter((part) => part && part.trim() !== '');
	return parts.join(', ');
};

const getPreviousCanadianAddressLines = (addressHistories: AddressHistory[]): string[] => {
	if (!addressHistories || addressHistories.length === 0) return [];
	const current = addressHistories.find((addr) => !addr.toDate);
	return addressHistories
		.filter((addr) => addr !== current)
		.filter((addr) => {
			const c = (addr.country || '').trim().toUpperCase();
			return c === 'CAN' || c === 'CA' || c === 'CANADA' || c === '';
		})
		.sort((a, b) => {
			const aDate = a.toDate ? new Date(a.toDate).getTime() : 0;
			const bDate = b.toDate ? new Date(b.toDate).getTime() : 0;
			return bDate - aDate;
		})
		.map(formatAddressLine)
		.filter((line) => line.length > 0);
};

const getCanadaSexCheckboxes = (gender?: string): React.ReactNode => {
	const normalized = (gender || '').trim().toLowerCase();
	const femaleChecked = normalized === 'female' || normalized === 'f';
	const maleChecked = normalized === 'male' || normalized === 'm';
	return (
		<>
			{femaleChecked ? '☑' : '☐'} Female &nbsp;&nbsp; {maleChecked ? '☑' : '☐'} Male
		</>
	);
};

const getCanadaPhotoIdTypes = (workflow: Workflow): string => {
	const types: string[] = [];
	if (
		workflow.passportDetails &&
		(workflow.passportDetails.number || workflow.passportDetails.passport || workflow.passportDetails.document)
	) {
		types.push('Passport');
	}
	if (
		workflow.driverLicenceDetails &&
		(workflow.driverLicenceDetails.number ||
			workflow.driverLicenceDetails.cardNumber ||
			workflow.driverLicenceDetails.document)
	) {
		types.push("Driver's Licence");
	}
	const idDocs = workflow.identityDocuments;
	const addIdentityDoc = (doc?: { documentType?: string }) => {
		const docType = doc?.documentType?.trim();
		if (docType && !types.includes(docType)) types.push(docType);
	};
	addIdentityDoc(idDocs?.primaryDocument);
	addIdentityDoc(idDocs?.firstSecondaryDocument);
	addIdentityDoc(idDocs?.secondSecondaryDocument);
	return types.join(', ');
};

const canadaConsentContent = (
	primaryName: PrimaryName,
	timeStamp: Date,
	image: string,
	workflow: Workflow,
	candidatePhone?: string,
	customerName?: string,
	currentUser?: User,
	ownerUser?: User
) => {
	const previousAddressLines = getPreviousCanadianAddressLines(workflow.addressHistories);
	const ownerName = ownerUser
		? getFullNameString({ firstName: ownerUser.firstName, lastName: ownerUser.lastName })
		: workflow.customerUserName || '';
	const ownerPhone = ownerUser?.phoneNumber || '';
	const witnessName = currentUser
		? getFullNameString({ firstName: currentUser.firstName, lastName: currentUser.lastName })
		: '';
	const photoIdTypes = getCanadaPhotoIdTypes(workflow);
	return (
		<>
			{/* Page 1: Informed Consent Form */}
			<div
				style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '8px', padding: '5px 8px', lineHeight: '1.15' }}
			>
				{/* Header with logo and title */}
				<div
					style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}
				>
					<div style={{ width: '110px' }}>
						<img src={aisLogo} alt="AIS Logo" style={{ width: '100%', height: 'auto' }} />
						<div style={{ fontSize: '7px', marginTop: '3px' }}>
							<strong>Accurate Information Systems, LLC</strong>
						</div>
					</div>
					<div style={{ textAlign: 'right' }}>
						<div style={{ fontSize: '11px', fontWeight: 'bold' }}>CRIMINAL RECORD VERIFICATION</div>
						<div style={{ fontSize: '9px', fontWeight: 'bold' }}>Informed Consent Form</div>
					</div>
				</div>

				{/* Section A: Personal Information */}
				<CanadaTable>
					<thead>
						<tr>
							<CanadaSectionHeaderCell colSpan={2}>A. Personal Information</CanadaSectionHeaderCell>
						</tr>
					</thead>
					<tbody>
						<tr>
							<CanadaFormCell>
								<div style={{ fontSize: '8.5px', marginBottom: '2px' }}>Surname (last name):</div>
								<div>{primaryName.lastName}</div>
							</CanadaFormCell>
							<CanadaFormCell>
								<div style={{ fontSize: '8.5px', marginBottom: '2px' }}>Given name(s):</div>
								<div>
									{primaryName.firstName} {primaryName.middleNames || ''}
								</div>
							</CanadaFormCell>
						</tr>
						<tr>
							<CanadaFormCell>
								<div style={{ fontSize: '8.5px', marginBottom: '2px' }}>Surname (last name) at birth:</div>
								<div>{primaryName.lastName}</div>
							</CanadaFormCell>
							<CanadaFormCell>
								<div style={{ fontSize: '8.5px', marginBottom: '2px' }}>Former name(s):</div>
								<div>&nbsp;</div>
							</CanadaFormCell>
						</tr>
						<tr>
							<CanadaFormCell colSpan={2}>
								<div style={{ fontSize: '8.5px', marginBottom: '2px' }}>
									Place of birth (City, Province/State, Country):
								</div>
								<div>{workflow.birthDetails?.country}</div>
							</CanadaFormCell>
						</tr>
						<tr>
							<CanadaFormCell>
								<div style={{ fontSize: '8.5px', marginBottom: '2px' }}>Date of birth (YYYY-MM-DD):</div>
								<div>{workflow.birthDetails?.birthDate}</div>
							</CanadaFormCell>
							<CanadaFormCell>
								<div style={{ fontSize: '8.5px', marginBottom: '2px' }}>Sex (check one):</div>
								<div>{getCanadaSexCheckboxes(workflow.birthDetails?.gender)}</div>
							</CanadaFormCell>
						</tr>
						<tr>
							<CanadaFormCell>
								<div style={{ fontSize: '8.5px', marginBottom: '2px' }}>Phone number(s):</div>
								<div>{candidatePhone || '\u00A0'}</div>
							</CanadaFormCell>
							<CanadaFormCell>
								<div style={{ fontSize: '8.5px', marginBottom: '2px' }}>Email address:</div>
								<div>{workflow.candidateEmail || '\u00A0'}</div>
							</CanadaFormCell>
						</tr>
						<tr>
							<CanadaFormCell colSpan={2}>
								<div style={{ fontSize: '8.5px', fontWeight: 'bold', marginBottom: '3px' }}>Current Home Address</div>
								<div style={{ fontSize: '7.5px', color: '#666', marginBottom: '2px' }}>
									Number &nbsp; Street &nbsp; Apartment &nbsp; City &nbsp; Province/Territory/State &nbsp; Postal/ZIP
									code
								</div>
								<div style={{ borderBottom: '1px solid #000', minHeight: '15px', paddingBottom: '2px' }}>
									{getCurrentAddress(workflow.addressHistories)}
								</div>
							</CanadaFormCell>
						</tr>
						<tr>
							<CanadaFormCell colSpan={2}>
								<div style={{ fontSize: '8.5px', fontWeight: 'bold', marginBottom: '3px' }}>
									Previous Address(es) Within the Last 5 Years (attach additional page if necessary)
								</div>
								{previousAddressLines.length > 0 ? (
									previousAddressLines.map((line, idx) => (
										<div
											// eslint-disable-next-line react/no-array-index-key
											key={`prev-addr-${idx}`}
											style={{
												borderBottom: '1px solid #000',
												minHeight: '15px',
												paddingBottom: '2px',
												marginBottom: '4px'
											}}
										>
											{line}
										</div>
									))
								) : (
									<>
										<div
											style={{
												borderBottom: '1px solid #000',
												minHeight: '15px',
												paddingBottom: '2px',
												marginBottom: '4px'
											}}
										>
											&nbsp;
										</div>
										<div style={{ borderBottom: '1px solid #000', minHeight: '15px', paddingBottom: '2px' }}>
											&nbsp;
										</div>
									</>
								)}
							</CanadaFormCell>
						</tr>
					</tbody>
				</CanadaTable>

				{/* Section B: Reason for the Criminal Record Verification */}
				<CanadaTable style={{ marginTop: '6px' }}>
					<thead>
						<tr>
							<CanadaSectionHeaderCell colSpan={2}>
								B. Reason for the Criminal Record Verification
							</CanadaSectionHeaderCell>
						</tr>
					</thead>
					<tbody>
						<tr>
							<CanadaFormCell colSpan={2}>
								<div style={{ fontSize: '8.5px', marginBottom: '2px' }}>
									Reason for Request (example: Employment – Employer – Job Title):
								</div>
								<div>Employment, Building Access, Security access</div>
							</CanadaFormCell>
						</tr>
						<tr>
							<CanadaFormCell colSpan={2}>
								<div style={{ fontSize: '8.5px', marginBottom: '2px' }}>Organization Requesting Search:</div>
								<div>{customerName || '\u00A0'}</div>
							</CanadaFormCell>
						</tr>
						<tr>
							<CanadaFormCell>
								<div style={{ fontSize: '8.5px', marginBottom: '2px' }}>Contact Name:</div>
								<div>{ownerName || ' '}</div>
							</CanadaFormCell>
							<CanadaFormCell>
								<div style={{ fontSize: '8.5px', marginBottom: '2px' }}>Contact Phone Number:</div>
								<div>{ownerPhone || ' '}</div>
							</CanadaFormCell>
						</tr>
					</tbody>
				</CanadaTable>

				{/* Section C: Informed Consent */}
				<CanadaTable style={{ marginTop: '6px' }}>
					<thead>
						<tr>
							<CanadaSectionHeaderCell>C. Informed Consent</CanadaSectionHeaderCell>
						</tr>
					</thead>
					<tbody>
						<tr>
							<CanadaFormCell>
								<p style={{ lineHeight: '1.3', marginBottom: '6px', fontSize: '8.5px' }}>
									<strong>SEARCH AUTHORIZATION</strong> – I HEREBY CONSENT TO THE SEARCH OF the RCMP National Repository
									of Criminal Records based on the name(s), date of birth and where used, the declared criminal record
									history provided by myself. I understand that this verification of the National Repository of Criminal
									Records is not being confirmed by fingerprint comparison which is the only true means by which to
									confirm if a criminal record exists in the National Repository of Criminal Records.
								</p>

								<p style={{ lineHeight: '1.3', marginBottom: '4px', fontSize: '8.5px' }}>
									<strong>POLICE INFORMATION SYSTEM(S)</strong> – I HEREBY CONSENT TO THE SEARCH OF police information
									systems, as part of a Police Information Check, which will consist of a search of the following
									systems (check applicable):
								</p>
								<div style={{ marginLeft: '20px', marginBottom: '6px', fontSize: '8.5px' }}>
									☐ CPIC Investigative Data Bank
								</div>

								<p style={{ lineHeight: '1.3', marginBottom: '6px', fontSize: '8.5px' }}>
									<strong>AUTHORIZATION AND WAIVER</strong> to provide a confirmation of criminal record or any police
									information.
								</p>

								<p style={{ lineHeight: '1.3', marginBottom: '6px', fontSize: '8.5px' }}>
									I certify that the information set out by me in this application is true and correct to the best of my
									ability. I consent to the release of the results of the criminal record checks to{' '}
									<span style={{ textDecoration: 'underline' }}>Accurate Information Systems, LLC</span>, located in{' '}
									<span style={{ textDecoration: 'underline' }}>Venice, USA</span>
								</p>

								<p style={{ lineHeight: '1.3', marginBottom: '8px', fontSize: '7.5px' }}>
									I hereby release and forever discharge all members and employees of the processing Police Service and
									the Royal Canadian Mounted Police from any and all actions, claims and demands for damages, loss or
									injury howsoever arising which may hereafter be sustained by myself as a result of the disclosure of
									information by the Brockville/Cobourg Police Service to Accurate Information Systems/Triton Venice
									USA/Toronto, Canada
								</p>

								<table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '5px' }}>
									<tbody>
										<tr>
											<td style={{ width: '40%', verticalAlign: 'top', padding: '3px' }}>
												<div style={{ fontSize: '8.5px', marginBottom: '3px' }}>Signature of Applicant</div>
												<div style={{ minHeight: '40px' }}>{image && <Image src={image} alt="signature" />}</div>
											</td>
											<td style={{ width: '30%', verticalAlign: 'top', padding: '3px' }}>
												<div style={{ fontSize: '8.5px', marginBottom: '3px' }}>Date</div>
												<div style={{ borderBottom: '1px solid #000', padding: '3px 0' }}>
													{safeFormat(timeStamp, 'yyyy-MM-dd')}
												</div>
												<div style={{ fontSize: '7.5px', color: '#666', marginTop: '2px' }}>Year – Month - Day</div>
											</td>
											<td style={{ width: '30%', verticalAlign: 'top', padding: '3px' }}>
												<div style={{ fontSize: '8.5px', marginBottom: '3px' }}>Signed at</div>
												<div style={{ borderBottom: '1px solid #000', padding: '3px 0' }}>
													{getCurrentCityProvince(workflow.addressHistories) || '\u00A0'}
												</div>
												<div style={{ fontSize: '7.5px', color: '#666', marginTop: '2px' }}>
													City &nbsp; Province/Territory
												</div>
											</td>
										</tr>
									</tbody>
								</table>
							</CanadaFormCell>
						</tr>
					</tbody>
				</CanadaTable>

				{/* Section D: Identification Verification */}
				<CanadaTable style={{ marginTop: '6px' }}>
					<thead>
						<tr>
							<CanadaSectionHeaderCell colSpan={2}>
								<strong>D. Identification Verification</strong> &nbsp;&nbsp;&nbsp; ☐ Physical Identity Verification
								&nbsp;&nbsp;&nbsp; ☑ Electronic Identity Verification
							</CanadaSectionHeaderCell>
						</tr>
					</thead>
					<tbody>
						<tr>
							<CanadaFormCell>
								<div style={{ fontSize: '8.5px', marginBottom: '2px' }}>Witnessing Agent&apos;s Name:</div>
								<div style={{ borderBottom: '1px solid #000', minHeight: '15px', paddingBottom: '2px' }}>
									{witnessName || ' '}
								</div>
							</CanadaFormCell>
							<CanadaFormCell>
								<div style={{ fontSize: '8.5px', marginBottom: '2px' }}>Identification Verified:</div>
								<div>☑ Yes &nbsp;&nbsp;&nbsp; ☐ No</div>
							</CanadaFormCell>
						</tr>
						<tr>
							<CanadaFormCell>
								<div style={{ fontSize: '8.5px', marginBottom: '2px' }}>Witnessing Agent&apos;s Signature:</div>
								<div
									style={{
										borderBottom: '1px solid #000',
										minHeight: '15px',
										paddingBottom: '2px',
										fontFamily: '"Brush Script MT", "Lucida Handwriting", "Segoe Script", cursive',
										fontSize: '16px',
										fontStyle: 'italic'
									}}
								>
									{witnessName || ' '}
								</div>
							</CanadaFormCell>
							<CanadaFormCell>
								<div style={{ fontSize: '8.5px', marginBottom: '2px' }}>Type of Photo ID Viewed</div>
								<div style={{ fontSize: '7.5px', color: '#666', marginBottom: '2px' }}>
									(Government Issued) & Secondary ID
								</div>
								<div style={{ borderBottom: '1px solid #000', minHeight: '15px', paddingBottom: '2px' }}>
									{photoIdTypes || ' '}
								</div>
							</CanadaFormCell>
						</tr>
					</tbody>
				</CanadaTable>

				<p style={{ fontSize: '8px', marginTop: '8px', lineHeight: '1.3' }}>
					Name and location of the company where information will be stored in Canada:{' '}
					<strong>Triton Canada Inc – Toronto, Ontario</strong>
				</p>

				<p style={{ fontSize: '7.5px', marginTop: '6px', textAlign: 'center', fontStyle: 'italic', lineHeight: '1.2' }}>
					**Information related to this criminal record check is collected, retained and disclosed in accordance with
					applicable privacy legislation. **
				</p>
			</div>

			{/* Page 2: Declaration of Criminal Record Form */}
			<div style={{ pageBreakBefore: 'always', marginTop: '50px', paddingTop: '20px' }}>
				<CanadaHeader style={{ textAlign: 'right', marginTop: '0' }}>CRIMINAL RECORD VERIFICATION</CanadaHeader>
				<CanadaSubHeader style={{ textAlign: 'right' }}>Declaration of Criminal Record Form</CanadaSubHeader>

				<AisContentWrapper style={{ fontSize: '10px', marginTop: '20px' }}>
					<div style={{ textAlign: 'center', marginBottom: '20px' }}>
						<strong style={{ fontSize: '12px' }}>Declaration of Criminal Record</strong>
					</div>

					<p style={{ lineHeight: '1.4', marginBottom: '15px' }}>
						This form is required to be filled and attached to your Informed Consent Form for a Criminal Record
						Verification.
					</p>

					<CanadaFormGrid>
						<CanadaFormRow>
							<CanadaFormField style={{ flex: 1 }}>
								<CanadaLabel>Surname (last name)</CanadaLabel>
								<CanadaValue>{primaryName.lastName}</CanadaValue>
							</CanadaFormField>
							<CanadaFormField style={{ flex: 1 }}>
								<CanadaLabel>Given name(s)</CanadaLabel>
								<CanadaValue>
									{primaryName.firstName} {primaryName.middleNames || ''}
								</CanadaValue>
							</CanadaFormField>
							<CanadaFormField style={{ flex: 1 }}>
								<CanadaLabel>Date of Birth:</CanadaLabel>
								<CanadaValue>{workflow.birthDetails?.birthDate}</CanadaValue>
								<div style={{ fontSize: '8px', color: '#666' }}>YYYY-MM-DD</div>
							</CanadaFormField>
						</CanadaFormRow>
					</CanadaFormGrid>

					<p style={{ lineHeight: '1.4', marginTop: '20px', marginBottom: '10px', fontSize: '9px' }}>
						Information is collected and disclosed in accordance with federal, provincial and municipal laws.
					</p>

					<p style={{ lineHeight: '1.4', marginBottom: '10px', fontSize: '9px' }}>
						A Declaration of Criminal Record does not constitute a Certified Criminal Record by the RCMP and may not
						contain all criminal record convictions.
					</p>

					<p style={{ lineHeight: '1.4', marginBottom: '10px' }}>
						<strong>Applicants must declare all convictions for offences under Canadian federal law.</strong>
					</p>

					<p style={{ lineHeight: '1.4', marginBottom: '5px' }}>
						<strong>Do not declare the following:</strong>
					</p>
					<ul
						style={{ fontSize: '9px', lineHeight: '1.5', marginLeft: '20px', marginBottom: '15px', paddingLeft: '0' }}
					>
						<li style={{ marginBottom: '5px' }}>
							A conviction for which you have received a Record Suspension (formerly pardon) in accordance with the{' '}
							<em>Criminal Records Act</em>;
						</li>
						<li style={{ marginBottom: '5px' }}>
							A conviction where you were a &quot;young person&quot; under the <em>Youth Criminal Justice Act</em>;
						</li>
						<li style={{ marginBottom: '5px' }}>
							An Absolute or Conditional Discharge, pursuant to section 730 of the <em>Criminal Code</em>;
						</li>
						<li style={{ marginBottom: '5px' }}>An offence for which you were not convicted;</li>
						<li style={{ marginBottom: '5px' }}>Any provincial or municipal offence, and;</li>
						<li style={{ marginBottom: '5px' }}>Any charges dealt with outside of Canada.</li>
					</ul>

					<p style={{ lineHeight: '1.4', marginBottom: '15px', fontSize: '9px', fontWeight: 'bold' }}>
						Note that a Certified Criminal Record can only be issued based on the submission of fingerprints to the RCMP
						National Repository of Criminal Records.
					</p>

					<CanadaDeclarationTable>
						<thead>
							<tr>
								<CanadaTableHeader>Offence</CanadaTableHeader>
								<CanadaTableHeader>Date of Sentence</CanadaTableHeader>
								<CanadaTableHeader>Court Location</CanadaTableHeader>
							</tr>
						</thead>
						<tbody>
							<tr>
								<CanadaTableCell>&nbsp;</CanadaTableCell>
								<CanadaTableCell>&nbsp;</CanadaTableCell>
								<CanadaTableCell>&nbsp;</CanadaTableCell>
							</tr>
							<tr>
								<CanadaTableCell>&nbsp;</CanadaTableCell>
								<CanadaTableCell>&nbsp;</CanadaTableCell>
								<CanadaTableCell>&nbsp;</CanadaTableCell>
							</tr>
							<tr>
								<CanadaTableCell>&nbsp;</CanadaTableCell>
								<CanadaTableCell>&nbsp;</CanadaTableCell>
								<CanadaTableCell>&nbsp;</CanadaTableCell>
							</tr>
							<tr>
								<CanadaTableCell>&nbsp;</CanadaTableCell>
								<CanadaTableCell>&nbsp;</CanadaTableCell>
								<CanadaTableCell>&nbsp;</CanadaTableCell>
							</tr>
						</tbody>
					</CanadaDeclarationTable>

					<CanadaFormGrid style={{ marginTop: '40px' }}>
						<CanadaFormRow>
							<CanadaFormField style={{ flex: 1 }}>
								<CanadaLabel>Signature of Applicant</CanadaLabel>
								<div style={{ minHeight: '50px', paddingTop: '5px' }}>
									{image && <Image src={image} alt="signature" />}
								</div>
							</CanadaFormField>
							<CanadaFormField style={{ flex: 1 }}>
								<CanadaLabel>Date (YYYY-MM-DD)</CanadaLabel>
								<CanadaValue>{safeFormat(timeStamp, 'yyyy-MM-dd')}</CanadaValue>
							</CanadaFormField>
						</CanadaFormRow>
					</CanadaFormGrid>
				</AisContentWrapper>
			</div>
		</>
	);
};

// China consent content (bilingual) - Complete form with page structure
const chinaConsentContent = (primaryName: PrimaryName, timeStamp: Date, image: string, workflow: Workflow) => (
	<>
		{/* Page 1: Personal Details Sheet with Table and Authorization */}
		<ChinaHeaderWrapper>
			<ChinaHeader>People&apos;s Republic of China</ChinaHeader>
			<div style={{ fontSize: '12px', marginTop: '8px' }}>
				Personal Details Sheet for Screening Purposes for PRC citizens
			</div>
			<div style={{ fontSize: '12px', marginTop: '5px' }}>中国公民个人资料表格 (适用于聘用前调查)</div>
		</ChinaHeaderWrapper>

		<div style={{ textAlign: 'center', fontSize: '11px', margin: '15px 0' }}>
			<div style={{ marginBottom: '5px' }}>
				Please complete all requested details <strong>in Chinese</strong>.
			</div>
			<div>请用中文填写以下资料，勿留空项。</div>
		</div>

		<ChinaTable>
			<tbody>
				<tr>
					<ChinaTableCell>
						<strong>Names in Chinese</strong>
						<br />
						<strong>and Former Name (if applicable)</strong>
						<br />
						(中文姓名及曾用名(如适用))
					</ChinaTableCell>
					<ChinaTableCellValue>
						{primaryName.firstName} {primaryName.lastName}
					</ChinaTableCellValue>
				</tr>
				<tr>
					<ChinaTableCell>
						<strong>PRC Identity Number and DOB</strong>
						<br />
						(中国身份证号码和出生日期)
					</ChinaTableCell>
					<ChinaTableCellValue>
						<div>身份证号码: {workflow.icrmDetails?.secondaryIdentificationNumber || '___________________'}</div>
						<div style={{ marginTop: '5px' }}>
							出生日期: {workflow.birthDetails?.birthDate || '___________________'}
						</div>
					</ChinaTableCellValue>
				</tr>
				<tr>
					<ChinaTableCell>
						<strong>Hukou Address, Name of Police</strong>
						<br />
						<strong>Office and Hukou Type</strong>
						<br />
						(户口地址、辖区派出所名称及户口类型)
					</ChinaTableCell>
					<ChinaTableCellValue>
						<div>户口地址：</div>
						<div style={{ marginTop: '5px' }}>辖区派出所名称：</div>
						<div style={{ marginTop: '5px' }}>○农业户口 ○城镇户口；○集体户口</div>
						<div style={{ marginTop: '5px' }}>户口所在地：○城镇户口；住址名称：</div>
					</ChinaTableCellValue>
				</tr>
				<tr>
					<ChinaTableCell>
						<strong>Entity that holds the official personal</strong>
						<br />
						<strong>record file, Contact Information & File</strong>
						<br />
						<strong>No.</strong>
						<br />
						(档案保管单位及联系方式、档案编号)
					</ChinaTableCell>
					<ChinaTableCellValue>
						<div>名称：</div>
						<div style={{ marginTop: '5px' }}>地址：</div>
						<div style={{ marginTop: '5px' }}>○以本人名义档案、档案号码：</div>
						<div style={{ marginTop: '5px' }}>○以单位名义档案、单位名称：</div>
					</ChinaTableCellValue>
				</tr>
				<tr>
					<ChinaTableCell>
						<strong>Can a reference be taken now?</strong>
						<br />
						<strong>If no, when can it be taken?</strong>
						<br />
						(现在，何时可与该人取得联系及方式)
					</ChinaTableCell>
					<ChinaTableCellValue>
						<div>○Yes (是) ○No (否)</div>
						<div style={{ marginTop: '5px' }}>(现在，何时可与该人取得联系及方式)</div>
					</ChinaTableCellValue>
				</tr>
				<tr>
					<ChinaTableCell>
						<strong>Details of Graduates whose</strong>
						<br />
						<strong>personal record file are kept at the</strong>
						<br />
						<strong>university only</strong>
						<br />
						(大学毕业的学校保管专业及及毕业年份)
					</ChinaTableCell>
					<ChinaTableCellValue>
						<div>学校：专业：</div>
						<div style={{ marginTop: '5px' }}>年级、学号：</div>
						<div style={{ marginTop: '5px' }}>○请提供复印件：</div>
					</ChinaTableCellValue>
				</tr>
			</tbody>
		</ChinaTable>

		<div style={{ marginTop: '35px' }}>
			<div style={{ fontSize: '14px', fontWeight: 'bold', textAlign: 'center', marginBottom: '20px' }}>授权书</div>
			<div style={{ fontSize: '12px', marginBottom: '12px' }}>致有关人士：</div>
			<div style={{ fontSize: '12px', lineHeight: '1.6', marginBottom: '12px', textAlign: 'justify' }}>
				本人授权此授权书的持有人对本人在职位申请表及个人履历中所提供的信息在其认为必要时进行核实。于核实有无犯罪记录时，请有关部门和单位予以配合。
			</div>
			<div style={{ fontSize: '12px', lineHeight: '1.6', marginBottom: '20px', textAlign: 'justify' }}>
				本人授权持有或了解有关情况的人士向此授权书持有人如实披露有关情况。本人免除其因此而可能产生的责任。
			</div>
			<div style={{ fontSize: '10px', fontStyle: 'italic', lineHeight: '1.6', marginBottom: '25px' }}>
				<strong>English Translation (for reference only):</strong> I hereby provide my authorization to the Bearer of
				this Letter to verify information provided in my resume and application of employment. I authorize all persons
				who may have information relevant to this enquiry to disclose it to the Bearer of this letter. I release all
				persons from liability on account of such disclosure.
			</div>
		</div>

		<ChinaSignatureTable style={{ marginTop: '30px', marginBottom: '25px' }}>
			<tbody>
				<tr>
					<td style={{ width: '15%', fontSize: '11px', padding: '12px', border: '1px solid #000' }}>
						姓名
						<br />
						Signed in Chinese
					</td>
					<td style={{ width: '35%', fontSize: '11px', padding: '12px', border: '1px solid #000' }}>
						: {image && <Image src={image} alt="signature" />}
					</td>
					<td style={{ width: '15%', fontSize: '11px', padding: '12px', border: '1px solid #000' }}>
						日期
						<br />
						Date
					</td>
					<td style={{ width: '35%', fontSize: '11px', padding: '12px', border: '1px solid #000' }}>
						: {safeFormat(timeStamp, 'dd/MM/yyyy')}
					</td>
				</tr>
			</tbody>
		</ChinaSignatureTable>

		<div style={{ fontSize: '12px', fontWeight: 'bold', marginTop: '25px', marginBottom: '30px' }}>QR Form 101</div>

		{/* Page 2: English Translation - Force page break with very large margin */}
		<div style={{ marginTop: '100px' }}>
			<div style={{ fontSize: '14px', textAlign: 'center', marginBottom: '40px' }}>
				English Translation (for reference only)
			</div>
			<div style={{ fontSize: '17px', fontWeight: 'bold', textAlign: 'center', marginBottom: '30px' }}>
				Letter of Authorization
			</div>
			<div style={{ fontSize: '12px', lineHeight: '1.8', margin: '0 50px', textAlign: 'justify' }}>
				<p style={{ marginBottom: '20px' }}>
					<strong>To Whom It May Concern:</strong>
				</p>
				<p style={{ marginBottom: '20px' }}>
					I hereby provide my authorization to the Bearer of this Letter to verify information provided in my resume and
					application of employment and to conduct enquiries as may be necessary, at the Bearer&apos;s discretion.
					Should the Bearer make application to the relevant department for a criminal record check, please do not
					hesitate to help.
				</p>
				<p style={{ marginBottom: '20px' }}>
					I authorize all persons who may have information relevant to this enquiry to disclose it to the Bearer of this
					letter. I release all persons from liability on account of such disclosure.
				</p>
			</div>
		</div>
	</>
);

// France consent content (French)
const franceConsentContent = (primaryName: PrimaryName, timeStamp: Date, image: string, workflow: Workflow) => (
	<>
		<FranceHeaderWrapper>
			<FranceHeader>MINISTERE DE LA JUSTICE</FranceHeader>
			<FranceSubHeader>DEMANDE D&apos;EXTRAIT DU CASIER JUDICIAIRE, BULLETIN N°3</FranceSubHeader>
			<FranceSubHeaderEng>(Application for a Criminal Record, Bulletin #3)</FranceSubHeaderEng>
		</FranceHeaderWrapper>

		{/* Top section with "Adressez votre demande" header */}
		<div
			style={{
				border: '1px solid #000',
				padding: '10px',
				marginBottom: '2px',
				textAlign: 'center',
				fontSize: '10px',
				fontWeight: 'bold'
			}}
		>
			Adressez votre demande / Send your application
		</div>

		{/* "Seul le Bulletin" notice */}
		<div
			style={{
				border: '1px solid #000',
				borderTop: 'none',
				padding: '12px 10px',
				marginBottom: '2px',
				fontSize: '9px',
				lineHeight: '1.5'
			}}
		>
			<div style={{ marginBottom: '6px' }}>
				<strong>Seul le Bulletin N°3 peut être obtenu et uniquement par la personne qu&apos;il concerne</strong>
			</div>
			<div style={{ fontSize: '8px', marginBottom: '10px' }}>
				(Articles 774 et suivants du Code de Procédure Pénale)
			</div>
			<div style={{ fontStyle: 'italic', fontSize: '8px', marginBottom: '3px' }}>
				Only Bulletin #3 may be obtained in person by the person concerned
			</div>
			<div style={{ fontStyle: 'italic', fontSize: '8px' }}>
				(Articles 774 and following of the French Criminal Code)
			</div>
		</div>

		{/* Instructions Box with Table */}
		<div style={{ border: '1px solid #000', borderTop: 'none', marginBottom: '15px' }}>
			<FranceInstructionsTable>
				<tbody>
					<tr>
						<FranceInstructionsCell style={{ borderTop: 'none', borderLeft: 'none' }}>
							<div style={{ marginBottom: '6px', fontSize: '9px' }}>
								<strong>
									Si vous êtes né(e) en France Métropolitaine, dans un Département d&apos;Outre-Mer ou à l&apos;étranger
								</strong>
							</div>
							<div style={{ fontSize: '8px', fontStyle: 'italic', marginBottom: '12px' }}>
								If you were born in Metropolitan France, in an Overseas Department or abroad
							</div>
							<div style={{ fontSize: '9px' }}>
								<strong>Casier Judiciaire National</strong>
								<br />
								44317 Nantes CEDEX 3
							</div>
						</FranceInstructionsCell>
						<FranceInstructionsCell style={{ borderTop: 'none', borderRight: 'none' }}>
							<div style={{ marginBottom: '6px', fontSize: '9px' }}>
								<strong>Si vous êtes né(e) dans un Territoire d&apos;Outre-Mer ou une Collectivité Territoriale</strong>
							</div>
							<div style={{ fontSize: '8px', fontStyle: 'italic', marginBottom: '12px' }}>
								If you were born in a French Overseas Territory or in a Territorial Collectivity
							</div>
							<div style={{ fontSize: '9px' }}>
								<strong>Procureur de la Première Juridiction</strong>
								<br />
								de votre lieu de naissance
								<br />
								<span style={{ fontSize: '8px', fontStyle: 'italic' }}>of your place of birth</span>
							</div>
						</FranceInstructionsCell>
					</tr>
				</tbody>
			</FranceInstructionsTable>
		</div>

		{/* Fill out instructions */}
		<div style={{ fontSize: '9px', marginBottom: '15px', lineHeight: '1.4' }}>
			<div style={{ marginBottom: '4px' }}>
				<strong>Remplissez complètement et lisiblement toutes les rubriques</strong> (en caractères d&apos;imprimerie -
				une lettre par case)
			</div>
			<div style={{ fontSize: '8px', fontStyle: 'italic' }}>
				<strong>Fill out all areas completely and legibly</strong> (in capital letters - one letter per section)
			</div>
		</div>

		{/* Main Form Section */}
		<FranceFormBox>
			<FranceFormHeader>
				État Civil du demandeur ou de l&apos;enfant mineur pour lequel le Bulletin est demandé
				<br />
				<span style={{ fontSize: '9px', fontStyle: 'italic' }}>
					Civil status of the applicant or minor child the bulletin is for
				</span>
			</FranceFormHeader>

			<FranceFormRow>
				<FranceFormLabel>
					nom <span style={{ fontSize: '9px', fontStyle: 'italic' }}>(last name)</span>:
				</FranceFormLabel>
				<FranceFormValue>{primaryName.lastName}</FranceFormValue>
			</FranceFormRow>

			<FranceFormRow>
				<FranceFormLabel>
					prénom(s) <span style={{ fontSize: '9px', fontStyle: 'italic' }}>(first name(s))</span>:
				</FranceFormLabel>
				<FranceFormValue>
					{primaryName.firstName} {primaryName.middleNames || ''}
				</FranceFormValue>
			</FranceFormRow>

			<FranceFormRow>
				<FranceFormLabel>
					nom d&apos;époux (s&apos;il y a lieu){' '}
					<span style={{ fontSize: '9px', fontStyle: 'italic' }}>(husband&apos;s last name (if applicable))</span>:
				</FranceFormLabel>
				<FranceFormValue />
			</FranceFormRow>

			<FranceFormRow>
				<FranceFormLabel>
					né(e) le (j-m-a) <span style={{ fontSize: '9px', fontStyle: 'italic' }}>(born (d-m-y))</span>:
				</FranceFormLabel>
				<FranceFormValue>{workflow.birthDetails?.birthDate}</FranceFormValue>
				<FranceFormLabel style={{ marginLeft: '20px' }}>
					à <span style={{ fontSize: '9px', fontStyle: 'italic' }}>(in)</span>:
				</FranceFormLabel>
				<FranceFormValue style={{ flex: 2 }}>{workflow.birthDetails?.country}</FranceFormValue>
			</FranceFormRow>

			<FranceFormRow>
				<FranceFormLabel>
					n° Département <span style={{ fontSize: '9px', fontStyle: 'italic' }}>(Department)</span>:
				</FranceFormLabel>
				<FranceFormValue style={{ width: '100px' }} />
				<FranceFormLabel style={{ marginLeft: '20px' }}>
					DOM-TOM ou pays étranger{' '}
					<span style={{ fontSize: '9px', fontStyle: 'italic' }}>(DOM-TOM or country of birth)</span>:
				</FranceFormLabel>
				<FranceFormValue style={{ flex: 2 }} />
			</FranceFormRow>

			<FranceFormRow>
				<FranceFormLabel>
					Filiation <span style={{ fontSize: '9px', fontStyle: 'italic' }}>(filiation)</span> de{' '}
					<span style={{ fontSize: '9px', fontStyle: 'italic' }}>(from)</span>:
				</FranceFormLabel>
				<FranceFormValue>{workflow.birthDetails?.fatherName}</FranceFormValue>
			</FranceFormRow>

			<FranceFormRow>
				<FranceFormLabel>
					et de <span style={{ fontSize: '9px', fontStyle: 'italic' }}>(and from)</span>:
				</FranceFormLabel>
				<FranceFormValue>{workflow.birthDetails?.motherName}</FranceFormValue>
			</FranceFormRow>
		</FranceFormBox>

		{/* Address Section */}
		<FranceFormBox style={{ marginTop: '15px' }}>
			<FranceFormHeader>
				Adresse où doit être expédié le bulletin{' '}
				<span style={{ fontSize: '9px', fontStyle: 'italic' }}>Address where the Bulletin must be sent</span>
			</FranceFormHeader>

			<FranceFormRow>
				<FranceFormLabel>
					n° <span style={{ fontSize: '9px', fontStyle: 'italic' }}>(no)</span>:
				</FranceFormLabel>
				<FranceFormValue style={{ width: '100px' }}>
					{getCurrentAddressHistory(workflow.addressHistories)?.streetNumber || ''}
				</FranceFormValue>
				<FranceFormLabel style={{ marginLeft: '20px' }}>
					rue <span style={{ fontSize: '9px', fontStyle: 'italic' }}>(street)</span>:
				</FranceFormLabel>
				<FranceFormValue style={{ flex: 2 }}>
					{getCurrentAddressHistory(workflow.addressHistories)?.streetName || ''}
				</FranceFormValue>
			</FranceFormRow>

			<FranceFormRow>
				<FranceFormLabel>
					commune <span style={{ fontSize: '9px', fontStyle: 'italic' }}>(city, state)</span>:
				</FranceFormLabel>
				<FranceFormValue>
					{getCurrentAddressHistory(workflow.addressHistories)?.locality ||
						getCurrentAddressHistory(workflow.addressHistories)?.suburb ||
						''}
				</FranceFormValue>
			</FranceFormRow>

			<FranceFormRow>
				<FranceFormLabel>
					code postal <span style={{ fontSize: '9px', fontStyle: 'italic' }}>(zip code)</span>:
				</FranceFormLabel>
				<FranceFormValue style={{ width: '150px' }}>
					{getCurrentAddressHistory(workflow.addressHistories)?.postcode || ''}
				</FranceFormValue>
				<FranceFormLabel style={{ marginLeft: '20px' }}>
					bureau distributeur <span style={{ fontSize: '9px', fontStyle: 'italic' }}>(post office)</span>:
				</FranceFormLabel>
				<FranceFormValue style={{ flex: 2 }} />
			</FranceFormRow>
		</FranceFormBox>

		{/* Number of Copies Section */}
		<FranceFormBox style={{ marginTop: '15px' }}>
			<FranceFormRow>
				<FranceFormLabel>
					Nombre d&apos;extraits demandés{' '}
					<span style={{ fontSize: '9px', fontStyle: 'italic' }}>number of copies requested</span>: ___
				</FranceFormLabel>
			</FranceFormRow>

			<FranceFormRow>
				<div style={{ fontSize: '10px', marginTop: '5px' }}>
					___ cochez si le Bulletin N°3 est destiné à être utilisé à l&apos;étranger
					<br />
					<span style={{ fontSize: '9px', fontStyle: 'italic', marginLeft: '20px' }}>
						check if the Bulletin #3 is to be used abroad (outside of France)
					</span>
				</div>
			</FranceFormRow>

			<FranceFormRow style={{ marginTop: '10px' }}>
				<div style={{ fontSize: '10px' }}>
					<strong>ne pas</strong> joindre d&apos;enveloppe ou de timbre pour le retour{' '}
					<span style={{ fontSize: '9px', fontStyle: 'italic' }}>
						<strong>do not</strong> enclose any stamp or envelope for return
					</span>
				</div>
			</FranceFormRow>

			<FranceFormRow style={{ marginTop: '15px', borderTop: '1px solid #000', paddingTop: '10px' }}>
				<FranceFormLabel>
					le (jour-mois-an) <span style={{ fontSize: '9px', fontStyle: 'italic' }}>(day-month-year)</span>:
				</FranceFormLabel>
				<FranceFormValue style={{ width: '150px' }}>{safeFormat(timeStamp, 'dd/MM/yyyy')}</FranceFormValue>
				<FranceFormLabel style={{ marginLeft: '40px' }}>
					signature du demandeur{' '}
					<span style={{ fontSize: '9px', fontStyle: 'italic' }}>applicant&apos;s signature</span>:
				</FranceFormLabel>
				<FranceFormValue>{image && <Image src={image} alt="signature" style={{ height: '50px' }} />}</FranceFormValue>
			</FranceFormRow>
		</FranceFormBox>

		{/* Footer Note */}
		<div style={{ fontSize: '8px', marginTop: '20px', lineHeight: '1.4', padding: '0 10px' }}>
			<div style={{ marginBottom: '8px' }}>
				<strong>ART. 781 DU CODE DE PROCÉDURE PÉNALE</strong> – la loi punit d&apos;une peine d&apos;amende quiconque,
				en prenant un faux nom ou une fausse qualité, s&apos;est fait délivrer un extrait du casier judiciaire d&apos;un
				tiers.
			</div>
			<div style={{ fontStyle: 'italic', fontSize: '8px' }}>
				<strong>ART 781 OF THE PROCEDURE PENAL CODE</strong> – the law punishes by fine whomever takes a false name or
				false identity in order to obtain a criminal record for a third party.
			</div>
		</div>
	</>
);

// Germany consent content (German)
const germanyConsentContent = (
	primaryName: PrimaryName,
	timeStamp: Date,
	image: string,
	workflow: Workflow,
	customerName: string
) => (
	<>
		<AisHeaderWrapper>
			<AisHeader3>Consumer Report / Investigative Consumer Report</AisHeader3>
			<div style={{ fontSize: '11px', marginTop: '5px' }}>
				Offenlegung und Veröffentlichung von Informationen Bevollmächtigung
			</div>
			<div style={{ fontSize: '11px', marginTop: '5px', fontWeight: 'bold' }}>Seite 1 von 2</div>
		</AisHeaderWrapper>
		<AisContentWrapper>
			<p style={{ fontSize: '10px', lineHeight: '1.6', marginBottom: '10px' }}>
				Ich authorisiere frei und eindeutig <strong>{customerName || '_____________________________________'}</strong>{' '}
				und (1) <strong>Accurate Information Systems, Inc.</strong>, ein Verbraucher-Berichterstattung Agentur, um
				Informationen aus allen Personal abzurufen, Bildungseinrichtungen, Behörden, Firmen, Unternehmen,
				Kreditauskunfteien, Strafverfolgungsbehörden auf Bundes-, Staats-oder Landkreis Ebene in Bezug auf meine
				bisherigen Tätigkeiten. Die erhalten informatischen können, aber nicht beschränkt auf, Hochschul-, Wohn-,
				Leistung, Arbeitsteilung, usw., die Teilnahme, Prozessinformation, Beschäftigungsgeschichte, persönliche
				Fähigkeiten Datensätze, Ich verstehe dass einige oder alle dieser Informationen über meine Vertreter Datenbanken
				abgerufen werden könn en und stehtenfalls solche Übertragung zu anderen beteiligten der internationale Grenzen
				identifiziert werden können und stehtenfalls solche übertragung in andere.
			</p>

			<p style={{ fontSize: '10px', lineHeight: '1.6', marginBottom: '10px' }}>
				Ich verstehe das ein Consumer Report oder Investigative Consumer Report (Consumer Report) kein vorbereitet
				werden um diese Informationen zu sammeln n / oder Hinweise kontaktiert werden, kann der Bericht bestehen
				ausschließlich aus Antrags-und der Lebensweise. Ich kann eine Kopie von einem Bericht erhalten, der über mich
				vorbereitet wird und kann auch die Art und den Inhalt aller Informationen in den Akten für mich vorbereitet
				wird, bevor oder während dem Bericht. Ich verstehe, dass ich das Recht habe, diese Datein innerhalb einer
				angemessenen Frist während der üblichen Geschäftszeiten zu inspizieren, und ich kann von einer anderen Person
				meiner Wahl gebeten, um den Inhalt meiner Datei zu erklären. Ich verstehe das ich angeforderte Kennzeichnung
				erforderten sind, und ich möchte meine Anfrage, direkt zu Accurate Information Systems unter einer der folgenden
				New York Büro: P.O. Box 6520 - Venice FL. 34284-6520 / NY Office - NY 11742 - Tel: 800-825-4109 / Fax:
				631-594-9354.
			</p>

			<p style={{ fontSize: '10px', lineHeight: '1.6', marginBottom: '10px' }}>
				Hiermit bestätige ich, das alle Aussagen und Antworten dargelegt auf dem Antragsformular und / oder meinen
				Lebenslauf wahrheitsgetreu und vollständig sind, nach meinem besten Wissen sind, und ich verstehe, dass, wenn
				die Beschäftigung im Anschluss an die Beschäftigungs Einstellung Überprüfung oder für wegen falscher
				Informationen durch Anforderung ist kein Versprechung Angebot zur Verfügung stehen, angemessene Zeitrahmen zu
				Anmietung Erwerbzuwekses, ich bin bereit, dass eine Fotokopie dieser Genehmigung mit der ursprünglichen ergänzen
				können. Die Genehmigung in Kraft bleiben während meiner Beschäftigung mit der oben genannten Firma beschäftigt
				werden (außer die jenigen die im US-Bundesstaat Kalifornien beschäftigt sind), wird diese Genehmigung in Kraft
				bleiben während dieser Beschäftigung.
			</p>

			<AisHeader4 style={{ marginTop: '15px', marginBottom: '10px' }}>BITTE DEUTLICH AUSFUELLEN</AisHeader4>

			<GermanyFormTable>
				<tbody>
					<tr>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>
							<strong>Nachname</strong>
						</GermanyTableCell>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>
							<strong>Vorname</strong>
						</GermanyTableCell>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>
							<strong>Mittelname</strong>
						</GermanyTableCell>
					</tr>
					<tr>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>{primaryName.lastName}</GermanyTableCell>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>{primaryName.firstName}</GermanyTableCell>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>
							{primaryName.middleNames || ''}
						</GermanyTableCell>
					</tr>
					<tr>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>
							<strong>Stasse</strong>
						</GermanyTableCell>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>
							<strong>Wohnart</strong>
						</GermanyTableCell>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>
							<strong>Bundesland</strong>
						</GermanyTableCell>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>
							<strong>Postleitzahl</strong>
						</GermanyTableCell>
					</tr>
					<tr>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>
							{[
								getCurrentAddressHistory(workflow.addressHistories)?.streetNumber,
								getCurrentAddressHistory(workflow.addressHistories)?.streetName
							]
								.filter(Boolean)
								.join(' ')}
						</GermanyTableCell>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>
							{getCurrentAddressHistory(workflow.addressHistories)?.locality ||
								getCurrentAddressHistory(workflow.addressHistories)?.suburb ||
								''}
						</GermanyTableCell>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>
							{getCurrentAddressHistory(workflow.addressHistories)?.state || ''}
						</GermanyTableCell>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>
							{getCurrentAddressHistory(workflow.addressHistories)?.postcode || ''}
						</GermanyTableCell>
					</tr>
					<tr>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>
							<strong>Fahrschein Nummer</strong>
						</GermanyTableCell>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>
							<strong>Ausstellungsort</strong>
						</GermanyTableCell>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>
							<strong>Läuft ab am</strong>
						</GermanyTableCell>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>
							<strong>Geburtsdatum</strong>
						</GermanyTableCell>
					</tr>
					<tr>
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }} />
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }} />
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }} />
						<GermanyTableCell style={{ borderBottom: '1px solid #000' }}>
							{workflow.birthDetails?.birthDate}
						</GermanyTableCell>
					</tr>
				</tbody>
			</GermanyFormTable>

			<p style={{ fontSize: '10px', marginTop: '15px', marginBottom: '8px' }}>
				Bitte geben Sie alle anderen <strong>Städte und Staaten</strong>, in denen Sie im Laufe der letzten 7 Jahre
				gelebt haben an.
			</p>

			<p style={{ fontSize: '10px', marginBottom: '15px' }}>
				Bitte geben Sie alle anderen <strong>Nachnamen</strong>, die Sie in den letzten 7 Jahren hatten.
			</p>

			<p style={{ fontSize: '10px', marginBottom: '15px' }}>
				Bitte geben Sie alle anderen <strong>Nachnamen</strong>, unter denen Sie Ihre GED, Abitur oder andere Diplomas
				erhalten haben.
			</p>

			<div style={{ fontSize: '10px', marginTop: '25px' }}>
				<div style={{ borderBottom: '1px solid #000', width: '100%', marginBottom: '15px' }} />
				<SignatureTable>
					<tbody>
						<tr>
							<td style={{ width: '33%', verticalAlign: 'top' }}>
								<div style={{ fontSize: '10px', marginBottom: '5px' }}>
									<strong>Unterschrift</strong>
								</div>
							</td>
							<td style={{ width: '34%', verticalAlign: 'top' }}>
								<div style={{ fontSize: '10px', marginBottom: '5px' }}>
									<strong>Nationale ID</strong>
								</div>
							</td>
							<td style={{ width: '33%', verticalAlign: 'top' }}>
								<div style={{ fontSize: '10px', marginBottom: '5px' }}>
									<strong>Datum</strong>
								</div>
							</td>
						</tr>
						<tr>
							<td style={{ verticalAlign: 'top' }}>
								<div style={{ minHeight: '60px' }}>
									{image && <Image src={image} alt="signature" style={{ height: '50px' }} />}
								</div>
							</td>
							<td style={{ verticalAlign: 'top' }}>
								<div
									style={{ fontSize: '8px', fontStyle: 'italic', color: '#666', lineHeight: '1.3', marginTop: '5px' }}
								>
									HINWEIS: Die folgenden Angaben sind freiwillig und wird nicht als Teil Ihrer Identifizierung bei der
									Überprüfung der Information verwendet.
								</div>
							</td>
							<td style={{ verticalAlign: 'top' }}>
								<div style={{ paddingTop: '5px', fontSize: '10px' }}>{safeFormat(timeStamp, 'dd/MM/yyyy')}</div>
							</td>
						</tr>
					</tbody>
				</SignatureTable>
			</div>

			<div style={{ fontSize: '9px', marginTop: '30px', textAlign: 'left' }}>074-8432-695 (5/110)</div>
		</AisContentWrapper>
	</>
);

// Saudi Arabia consent content (bilingual - Arabic and English)
const saudiArabiaConsentContent = (primaryName: PrimaryName, timeStamp: Date, image: string) => (
	<>
		<AisHeaderWrapper style={{ textAlign: 'center' }}>
			<AisHeader3 style={{ color: '#0066cc', fontSize: '16px', marginBottom: '10px' }}>
				AIS INTERNATIONAL GROUP
			</AisHeader3>
		</AisHeaderWrapper>

		{/* Page 1 - Declaration of Consent */}
		<div style={{ marginTop: '10px' }}>
			{/* Two-column layout for English and Arabic */}
			<div style={{ display: 'flex', gap: '20px', fontSize: '14px', lineHeight: '1.6', marginBottom: '20px' }}>
				{/* English column */}
				<div style={{ flex: 1, textAlign: 'justify' }}>
					<div style={{ fontWeight: 'bold' }}>Declaration of Consent</div>
					<p style={{ marginBottom: '15px' }}>
						In connection with this request I authorize AIS , its assigned sub processors and/or its subsidiaries, all
						governmental and private agencies within the North American Region, European Union, or any member state
						thereof, Asia Pacific (APAC); Middle East; South America or African regions, to release information they may
						have related to my criminal record, educational or employment record to the person or company with which
						this form has been filed, or their assigned sub processor for that purpose in which this consent was
						obtained. I authorize this consent to any nation or my home country of residence.
					</p>
					<p style={{ marginBottom: '15px' }}>
						I authorize AIS, an independent agent acting on behalf of my prospective employer, assigned sub processor or
						sub processor to verify information presented on my submitted form and in any submitted and/or attached
						documents. I understand that these documents may include sensitive personal data for the purposes of the UK
						Data Protection legislation, and the obtaining of documents and/or information which is covered by the
						European legislation (General Data Protection Regulation – GDPR-EEA) and/or by applicable data protection
						legislation which is relevant in any other country. I also understand and agree that, where my personal
						history requires background verifications to be carried out in countries where I have worked and/or resided,
						in respect of my criminality, employment or education or residence, my data may be sent and processed in
						those relevant countries and transferred in any format to countries outside the EEA, including those that do
						not have sufficient data protection standards as stated by the EEA authorities.
					</p>
				</div>

				{/* Arabic column */}
				<div style={{ flex: 1, textAlign: 'justify', direction: 'rtl', fontSize: '16px', lineHeight: '1.8' }}>
					<div style={{ direction: 'rtl', marginTop: '5px', fontWeight: 'bold' }}>تصريح بالموافقة</div>
					<p style={{ marginBottom: '50px' }}>
						بخصوص ھذا الطلب، فإنني أفوض &quot;أي آي اس&quot; AIS والمعالجین للبیانات من الباطن المعین و/أو الشركات
						التابعة لدیھا وكافة الوكالات الحكومیة والخاصة ضمن الاتحاد الأوروبي أو أي دولة عضو فیھ وآسیا والمحیط الھادئ
						(APAC]، ومنطقة الشرق الأوسط وأمریكا الشمالیة وأمریكا الجنوبیة أو المناطق الأفریقیة، وذلك لنشر معلومات قد
						تكون لدیھم فیما یتعلق بسجلي الجنائي أو سجلي التعلیمي أو التوظیفي إلى الشخص أو الشركة الذي/التي تم إیداع ھذا
						النموذج معھ/معھا أو معالج البیانات من الباطن لدیھما لذلك الغرض الذي فیھ تم الحصول على ھذه الموافقة. وأفوض
						ھذه الموافقة لأیة أمة أو بلدي الأم مكان إقامتي.
					</p>
					<p style={{ marginBottom: '50px' }}>
						كما أفوض AIS، بصفتھا الوكیل المستقل المتصرف نیابة حتمل لدي أو معالج البیانات ً عن الموظف الم أو معالج
						البیانات من الباطن للتحقق من المعلومات المقدمة في نموذجي على الانترنیت أو في أي مستندات مقدمة و/أو مرفقة.
						وأدرك بأن تلك المستندات قد تتضمن بیانات شخصیة حساسة لأغراض قانون حمایة البیانات البریطاني، والحصول على
						المستندات و/أو المعلومات المشمولة بموجب التشریع الأوروبي (لائحة حمایة البیانات العامة-GDPR ( و/أو بموجب
						تشریع حمایة بیانات معمول بھ ذا الصلة بأیة دولة أخرى. كما ،ً حیث یتطلب تاریخي الشخصي أدرك وأوافق أیضا تحقیقات
						خلفیة سیتم إجراؤھا في دول عملت و/أو أقمت فیھا، بصدد إجرامي أو عملي أو تعلیمي أو إقامتي، بأنھ یجوز أرسال
						ومعالجة بیاناتي في تلك الدول ذات الصلة وتحویلھا إلى أي نمط إلى البلدان خارج المنطقة الاقتصادیة الأوروبیة،
						بما فیھا تلك البلدان التي لیس لدیھا معاییر حمایة بیانات كافیة كما نصت علیھا السلطات في المنطقة الاقتصادیة
						الأوروبیة. وأدرك وأوافق على أن المعلومات الموفرة في ھذا النموذج والمتضمنة في المستندات ُ المرفقة، إذا طلبت،
						صحیحة وكاملة حسب أحسن معرفتي.
					</p>
				</div>
			</div>

			{/* Signature acknowledgment section */}
			<div style={{ display: 'flex', gap: '20px', fontSize: '14px', lineHeight: '1.6', marginBottom: '20px' }}>
				<div style={{ flex: 1, textAlign: 'justify' }}>
					I understand and agree the information provided on this form and that contained in attached documents, if
					required, is true and complete to the best of my knowledge.
					<p>
						My signature below acknowledges the fact that I have read, understand and freely give my unambiguous consent
						to the handling of my personal information. I agree that AIS or its assigned sub processor may contact the
						custodian(s) of record either by phone, email or regular mail for the purposes set out above. Furthermore, I
						hereby release the aforesaid parties or the Company or individuals that release information about me from
						any liability whatsoever in collecting and disseminating information about me from any liability whatsoever
						in collecting and disseminating the information obtained. I confirm that I have read and understood AIS
						Privacy Policy and am aware of my subject rights under GDPR.{' '}
						<a href="https://accinfosys.com/privacy-policy/" style={{ color: '#0066cc' }}>
							https://accinfosys.com/privacy-policy/
						</a>
					</p>
				</div>
				<div style={{ flex: 1, textAlign: 'justify', direction: 'rtl', fontSize: '16px', lineHeight: '1.8' }}>
					<p>
						كما يقر توقيعي أدناه بأنني قد قرأت وأدرك وأمنح بحرية موافقتي الصريحة للتعامل مع معلوماتي الشخصية. وأوافق
						بأنه يجوز لكل من كن AIS أو معالج البيانات المعينين لديها أن يتصلوا بأوصياء/الأوصياء على السجل إما عبر الهاتف
						أو البريد الإلكتروني أو البريد الطبيعي للأغراض المبينة أعلاه وعلاوة على ذلك، فإنني أقوم بإعفاء الأطراف
						المذكورة سلفاً أو الشركة أو الأشخاص الذين يفشرون المعلومات عني من أية مسؤولية مهما كانت في صدد تحصيل ونشر
						المعلومات المحصل عليها وأؤكد كذلك بأنني قرأت وفهمت سياسة الخصوصية الخاصة ب AIS وأنني مدرك لحقوق الموضوع
						المعنية لدي بموجب اللائحة العامة/
						<a href="https://accinfosys.com/privacy-policy/" style={{ color: '#0066cc', direction: 'ltr' }}>
							https://accinfosys.com/privacy-policy/
						</a>
					</p>
				</div>
			</div>

			{/* Information to be checked section */}
			<div style={{ marginTop: '30px', marginBottom: '20px' }}>
				<div style={{ display: 'flex', gap: '20px', fontSize: '14px', lineHeight: '1.5' }}>
					<div style={{ flex: 1 }}>
						<div style={{ fontWeight: 'bold' }}>The following information may be checked:</div>
						<p style={{ marginBottom: '10px' }}>
							<strong>Basic criminality:</strong> A basic check via the relevant jurisdictional agency containing the
							details of criminal convictions considered unspent under jurisdictional spent conviction laws.
						</p>
						<p>
							<strong>Educational/Employment confirmation:</strong> A request to the University/College or employer
							claimed in order to verify any/all details as they relate to your educational qualifications or past
							employment experience.
						</p>
					</div>
					<div style={{ flex: 1, direction: 'rtl', fontSize: '14px', lineHeight: '1.5' }}>
						<div style={{ direction: 'rtl', marginTop: '5px', fontWeight: 'bold' }}>
							يجوز التحقق من المعلومات التالية:
						</div>
						<p style={{ marginBottom: '10px' }}>
							<strong>الإجرامية الأساسية:</strong> تحقق أساسي من خلال الوكالة القضائية المعينة متضمناً تفاصيل الإدانات
							الجنائية التي تعتبر غير منقية بموجب القوانين القضائية المعنية بالإدانات المنقضية.
						</p>
						<p>
							<strong>التأكيد التعليم/التوظيفي:</strong> طلب موجه إلى الجامعة/الكلية أو صاحب العمل من أجل التحقق من أي /
							جميع التفاصيل المتعلقة بمؤهلاتك التعليمية أو خبرتك الوظيفية السابقة.
						</p>
					</div>
				</div>
			</div>

			{/* Signature section */}
			<SignatureSection style={{ marginTop: '40px' }}>
				<SignatureTable>
					<tbody>
						<tr>
							<td style={{ width: '50%' }}>
								<SignatureLabel>
									Applicant Name (please print)
									<br />
									<span style={{ direction: 'rtl' }}>(اسم مقدم الطلب (يرجى الطباعة</span>
								</SignatureLabel>
								<SignatureField>
									{primaryName.firstName} {primaryName.middleNames} {primaryName.lastName}
								</SignatureField>
							</td>
							<td style={{ width: '50%', paddingLeft: '20px' }}>
								<SignatureLabel>
									Date (month/day/year)
									<br />
									<span style={{ direction: 'rtl' }}>(التاريخ (الشهر/اليوم/السنة</span>
								</SignatureLabel>
								<SignatureField>{safeFormat(timeStamp, 'MM/dd/yyyy')}</SignatureField>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<SignatureLabel style={{ marginTop: '20px' }}>
									Applicant Signature
									<br />
									<span style={{ direction: 'rtl' }}>توقيع مقدم الطلب</span>
								</SignatureLabel>
								<SignatureField>{image && <Image src={image} alt="signature" />}</SignatureField>
							</td>
						</tr>
					</tbody>
				</SignatureTable>
			</SignatureSection>
		</div>
	</>
);

// Central and South America consent content (Spanish)
const latinAmericanConsentContent = (
	primaryName: PrimaryName,
	timeStamp: Date,
	image: string,
	workflow: Workflow,
	customerName: string
) => (
	<>
		<AisHeaderWrapper>
			<LatinAmericanHeader>CONSENTIMIENTO PARA COMPARTIR INFORMACION PERSONAL</LatinAmericanHeader>
			<LatinAmericanSubHeader>
				AUTORIZACION PARA REALIZAR INVESTIGACION DE ANTECEDENTES NO PENALES
			</LatinAmericanSubHeader>
		</AisHeaderWrapper>
		<AisContentWrapper>
			<p>
				CERTIFICO QUE LA INFORMACION PROPORCIONADA POR MI PERSONA EN ESTA SOLICITUD ES VERDADERA Y CORRECTA. CONSIENTO
				QUE SE REALICE LA INVESTIGACION CRIMINAL A{' '}
				<strong>{customerName || '_____________________________________'}</strong> Y SUS ASOCIADO. Al firmar esta forma
				estoy consciente de ello y doy mi permiso para que este historial se maneje de manera electronica o sea
				archivado en copia fisica para propositos laborales unicamente.
			</p>
			<p>
				Por la presente permito su realizacion y libero de cualquier responsabilidad a todo empleado de las oficinas de
				la Autoridad Competente de que se trate por cualquier cosa que pueda resultar al autorizar el compartir mi
				informacion por parte de la Autoridad competente con <strong>VETTING.com</strong>.
			</p>

			<SignatureSection>
				<SignatureTable>
					<tbody>
						<tr>
							<td colSpan={2}>
								<SignatureLabel>Firma de Solicitante:</SignatureLabel>
								<SignatureField>{image && <Image src={image} alt="signature" />}</SignatureField>
							</td>
						</tr>
						<tr>
							<td>
								<SignatureLabel>Fecha:</SignatureLabel>
								<SignatureField>{safeFormat(timeStamp, 'dd/MM/yyyy')}</SignatureField>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<SignatureLabel>Nombre:</SignatureLabel>
								<SignatureField>
									{primaryName.firstName} / {primaryName.lastName} / {primaryName.middleNames || ''}
								</SignatureField>
								<div style={{ fontSize: '10px', marginTop: '5px' }}>(Primero Apellido / Sec. Apellido / Nombres)</div>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<SignatureLabel># DE CEDULA IDENTIDAD:</SignatureLabel>
								<SignatureField>
									{workflow.icrmDetails?.secondaryIdentificationNumber || '_______________________'}
								</SignatureField>
							</td>
						</tr>
					</tbody>
				</SignatureTable>
			</SignatureSection>
		</AisContentWrapper>
	</>
);

// South America consent content (Spanish)
const southAmericanConsentContent = (
	primaryName: PrimaryName,
	timeStamp: Date,
	image: string,
	workflow: Workflow,
	customerName: string
) => (
	<>
		<AisHeaderWrapper style={{ textAlign: 'center' }}>
			<LatinAmericanHeader>CONSENTIMIENTO PARA COMPATIR INFORMACION PERSONAL.</LatinAmericanHeader>
			<LatinAmericanSubHeader>
				AUTORIZACION PARA REALIZAR INVESTIGATION DE ANTECEDENTES NO PENALES
			</LatinAmericanSubHeader>
		</AisHeaderWrapper>
		<AisContentWrapper style={{ textAlign: 'justify', padding: '20px 40px' }}>
			<p style={{ marginBottom: '20px' }}>
				CERTIFICO QUE LA INFROMACION PROPORCIONADA POR MI PERSONA EN ESTA SOLICITUD ES VERDADERA Y CORECTA. CONSIENTO
				QUE SE REALICE LA INVESTIGACION CRIMINAL A{' '}
				<strong style={{ textDecoration: 'underline' }}>
					{customerName || '_____________________________________'}
				</strong>{' '}
				Y SUS ASOCIADO. Al firmar esta forma estoy consciente de ello y doy mi permiso para que este historial se maneje
				de manera electronica o sea archivado en copia fisica para propositos laborales unicamente.
			</p>
			<p style={{ marginBottom: '30px' }}>
				Por la presente permit su realizacion y libero de cualquier responsabilidad a todo empleado de las oficinas de
				la Autoridad Competente de que se trate por cualquier cosa que pueda resultar al autorizar el compartir mi
				informacion por parte de la Authoridad competente con{' '}
				<strong style={{ textDecoration: 'underline' }}>VETTING.com</strong>.
			</p>

			<SignatureSection>
				<SignatureTable>
					<tbody>
						<tr>
							<td colSpan={2}>
								<SignatureLabel>Firma de Solicitante:</SignatureLabel>
								<SignatureField style={{ borderBottom: '1px solid #000', minHeight: '40px' }}>
									{image && <Image src={image} alt="signature" />}
								</SignatureField>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<SignatureLabel>Fecha:</SignatureLabel>
								<SignatureField style={{ borderBottom: '1px solid #000', minHeight: '30px' }}>
									{safeFormat(timeStamp, 'dd/MM/yyyy')}
								</SignatureField>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<SignatureLabel>Nombre:</SignatureLabel>
								<SignatureField
									style={{
										borderBottom: '1px solid #000',
										minHeight: '30px',
										display: 'flex',
										justifyContent: 'space-between'
									}}
								>
									<span style={{ flex: 1, textAlign: 'center', borderRight: '1px solid #000', padding: '5px' }}>
										{primaryName.firstName}
									</span>
									<span style={{ flex: 1, textAlign: 'center', borderRight: '1px solid #000', padding: '5px' }}>
										{primaryName.lastName}
									</span>
									<span style={{ flex: 1, textAlign: 'center', padding: '5px' }}>{primaryName.middleNames || ''}</span>
								</SignatureField>
								<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', marginTop: '5px' }}>
									<span style={{ flex: 1, textAlign: 'center' }}>Primero Apellido</span>
									<span style={{ flex: 1, textAlign: 'center' }}>Sec. Apellido</span>
									<span style={{ flex: 1, textAlign: 'center' }}>Nombres</span>
								</div>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<SignatureLabel># DE CEDUAL IDENTIDAD:</SignatureLabel>
								<SignatureField>
									{workflow.icrmDetails?.secondaryIdentificationNumber || '___________________________________________'}
								</SignatureField>
							</td>
						</tr>
					</tbody>
				</SignatureTable>
			</SignatureSection>
		</AisContentWrapper>
	</>
);

// South Africa consent content
const southAfricaConsentContent = (
	primaryName: PrimaryName,
	timeStamp: Date,
	image: string,
	workflow: Workflow,
	customerName: string
) => {
	const fullName = `${primaryName.lastName}, ${primaryName.firstName}${
		primaryName.middleNames ? ` ${primaryName.middleNames}` : ''
	}`;
	const identityNumber = workflow.icrmDetails?.secondaryIdentificationNumber || '';
	const employerName = customerName || '';
	const position = workflow.occupationDetails?.positionApplied || '';
	const dateLocation = workflow.addressHistories?.[0]?.locality || workflow.addressHistories?.[0]?.suburb || '';

	return (
		<>
			<AisHeaderWrapper>
				<div style={{ textAlign: 'center', marginBottom: '10px' }}>
					<div style={{ fontStyle: 'italic', fontSize: '14px', marginBottom: '5px' }}>INDEMNITY &quot;B&quot;</div>
					<AisHeader3>INDEMNITY BY APPLICANT/ EMPLOYEE</AisHeader3>
				</div>
			</AisHeaderWrapper>
			<AisContentWrapper>
				<div style={{ marginBottom: '20px', borderBottom: '1px solid black', paddingBottom: '5px' }}>
					I, <strong>{fullName}</strong>
					<div style={{ textAlign: 'center', fontSize: '12px', marginTop: '5px' }}>(Surname and first name/s)</div>
				</div>

				<div style={{ marginBottom: '20px' }}>
					<strong>IDENTITY NUMBER:</strong> <span style={{ marginLeft: '10px' }}>{identityNumber}</span>
				</div>

				<div style={{ marginBottom: '15px' }}>
					<div style={{ marginBottom: '10px' }}>Have applied for employment by/is currently employed by:</div>
					<div style={{ borderBottom: '1px solid black', marginBottom: '5px', paddingBottom: '5px' }}>
						<strong>{employerName}</strong> as employer,
					</div>
					<div style={{ marginTop: '10px' }}>
						in the capacity of <strong>{position}</strong>
						<div style={{ textAlign: 'center', fontSize: '12px', marginTop: '5px' }}>(state position applied for)</div>
					</div>
				</div>

				<div
					style={{ borderTop: '2px solid black', borderBottom: '2px solid black', padding: '15px 0', margin: '20px 0' }}
				>
					<p style={{ textAlign: 'justify', marginBottom: '15px' }}>
						I hereby give my permission to the above-named employer to take my fingerprints and to place it at the
						disposal of the South African police.
					</p>
					<p style={{ textAlign: 'justify', marginBottom: '15px' }}>
						Furthermore, I give explicit consent to the South African Police Criminal Record Centre to furnish
						information of myself regarding any criminal background, criminal history, previous convictions and/or any
						relevant information to the above employer as is usually furnished by the South African Police Criminal
						Record Centre on form SAP 69.
					</p>
					<p style={{ textAlign: 'justify', marginBottom: '15px' }}>
						Furthermore the undersigned also undertakes to indemnify the South African Police Criminal Record Centre and
						all its members or employees in the service of the South African Police, as well as the Government of the
						Republic of South Africa unconditionally against all liability and all actions, proceedings, claims,
						damages, interest, costs including attorney/client costs and expenses whatsoever in relation thereto or what
						result or may result from furnishing of information on the said from SAP 69.
					</p>
				</div>

				<div style={{ marginTop: '30px', marginBottom: '30px' }}>
					DATED at <strong>{dateLocation}</strong> this <strong>{safeFormat(timeStamp, 'dd')}</strong> day of{' '}
					<strong>{safeFormat(timeStamp, 'MMMM yyyy')}</strong>.
				</div>

				<div style={{ marginTop: '50px', textAlign: 'right' }}>
					<div style={{ borderTop: '1px solid black', display: 'inline-block', width: '300px', paddingTop: '5px' }}>
						<div style={{ fontSize: '11px', textAlign: 'center' }}>Signature of applicant/employee</div>
						{image && (
							<div style={{ textAlign: 'center', marginTop: '10px' }}>
								<Image src={image} alt="signature" />
							</div>
						)}
					</div>
				</div>
			</AisContentWrapper>
		</>
	);
};

// EU International consent (used by Ireland, Netherlands)
const euInternationalConsentContent = (
	primaryName: PrimaryName,
	timeStamp: Date,
	image: string,
	workflow: Workflow
) => (
	<>
		<AisHeaderWrapper>
			<IrelandAisHeader3>International Consent/Authorization</IrelandAisHeader3>
			<div style={{ fontSize: '16px', marginTop: '15px', fontWeight: 'bold' }}>Consent/Authorization</div>
		</AisHeaderWrapper>
		<IrelandAisContentWrapper>
			<p style={{ marginBottom: '15px', textAlign: 'justify' }}>
				In connection with this request I authorize all governmental and private agencies within the European Union, or
				any member state thereof, Asia Pacific (APAC); Middle East; South America or African regions, to release
				information they may have related to my criminal record, educational or employment record to the person or
				company with which this form has been filed, or their agent for that purpose in which this consent was obtained.
				I authorize this consent to any nation outside the E.U or my home country of{' '}
				{workflow.countryCode ? getCountryName(workflow.countryCode) : '_____________________________'}.
			</p>
			<p style={{ marginBottom: '15px', textAlign: 'justify' }}>
				I understand that data will not be transferred to any country that lies outside the European Economic Area
				(EEA); North America; Asia Pacific (APAC); Middle East; South America or African regions; however, data can be
				transferred with the consent of the data subject (applicant) at the point of collection where it is known that
				there will be a need or desire to transfer such data. Further, in accordance with the host nation laws regarding
				the release of information, the Fair Credit Reporting (FCRA), 15 U.S.C. 1681-1681u, Data Protection Privacy Act
				1998, European Directive on Data Protection 95/46/EC and others, I authorize the release and transmittal of
				information from any country to any required agency that may have a legitimate business need, and to a company
				that holds this consent or any of its affiliate companies should the need arise. I further authorize any agent,
				to the extent required by any laws, rules applicable to any country, to provide the data pursuant to this
				authorization to governmental authorities in any country in connection with any regulatory filings it may submit
				to authorities.
			</p>
			<p style={{ marginBottom: '15px', textAlign: 'justify' }}>
				I understand and agree the information provided on this form and that contained in attached documents, if
				required, is true and complete to the best of my knowledge.
			</p>
			<p style={{ marginBottom: '15px', textAlign: 'justify' }}>
				My signature below acknowledges the fact that I have read, understand and freely give my unambiguous consent to
				the handling of my personal information. Furthermore, I hereby release the aforesaid parties or the Company or
				individuals that release information about me from any liability whatsoever in collecting and disseminating the
				information obtained.
			</p>
			<p style={{ marginBottom: '15px', textAlign: 'justify' }}>
				In addition, if you are a resident of any E.U member state, the GDPR (General Data Protection Regulations)
				provides additional rights you may choose to apply at any time:
			</p>
			<div style={{ marginBottom: '15px', paddingLeft: '20px' }}>
				<p style={{ marginBottom: '8px' }}>
					1- The right to know if your personal data is being processed, where it being processed and for what reason it
					is being processed.
				</p>
				<p style={{ marginBottom: '8px' }}>2- The right to a free copy of all reports prepared on your behalf</p>
				<p style={{ marginBottom: '8px' }}>
					3- The right to be forgotten (Data Erasure), personal data will no longer be disseminated or become relevant
					to the original purpose for processing
				</p>
				<p style={{ marginBottom: '8px' }}>
					4- You have the right to transmit data we provide to you to other data controllers
				</p>
				<p style={{ marginBottom: '18px' }}>5- The right to notification of any data breaches</p>
			</div>
			<p style={{ marginBottom: '150px', marginTop: '30px', textAlign: 'justify' }}>
				It is our policy that all data becomes encrypted without the ability, unless a data encryption key held
				separately is used, to anonymize any and all information that may lead to the report to being identifiable with
				you. This encryption occurs immediately after the information is used for the purpose it was obtained.
			</p>
		</IrelandAisContentWrapper>

		<IrelandAisContentWrapper style={{ pageBreakBefore: 'always' }}>
			<div style={{ marginBottom: '30px', marginTop: '30px' }}>
				<p style={{ marginBottom: '15px' }}>
					Please initial as confirmation your wish to withdraw this consent immediately prior to any information being
					obtained _______________
				</p>
				<p style={{ marginBottom: '30px' }}>
					Please initial as confirmation your wish to withdraw this consent after the data is used for the purpose in
					which it was obtained________________
				</p>
			</div>

			<SignatureSection>
				<IrelandSignatureTable>
					<tbody>
						<tr>
							<td style={{ width: '60%', verticalAlign: 'bottom', paddingBottom: '5px' }}>
								<SignatureField style={{ borderBottom: '1px solid #000', minHeight: '30px', paddingBottom: '5px' }}>
									{primaryName.firstName} {primaryName.middleNames} {primaryName.lastName}
								</SignatureField>
								<SignatureLabel style={{ fontSize: '10px', marginTop: '3px' }}>
									Applicant Name (please print in black ink)
								</SignatureLabel>
							</td>
							<td style={{ width: '40%', verticalAlign: 'bottom', paddingBottom: '5px', paddingLeft: '20px' }}>
								<SignatureField style={{ borderBottom: '1px solid #000', minHeight: '30px', paddingBottom: '5px' }}>
									{safeFormat(timeStamp, 'MM/dd/yyyy')}
								</SignatureField>
								<SignatureLabel style={{ fontSize: '10px', marginTop: '3px' }}>Date (month/day/year)</SignatureLabel>
							</td>
						</tr>
						<tr>
							<td colSpan={2} style={{ verticalAlign: 'bottom', paddingBottom: '5px', paddingTop: '30px' }}>
								<SignatureField style={{ borderBottom: '1px solid #000', minHeight: '30px', paddingBottom: '5px' }}>
									{workflow.icrmDetails?.secondaryIdentificationNumber || ''}
								</SignatureField>
								<SignatureLabel style={{ fontSize: '10px', marginTop: '3px' }}>
									Government ID, PAN Card, or similar, if applicable
								</SignatureLabel>
							</td>
						</tr>
						<tr>
							<td colSpan={2} style={{ verticalAlign: 'bottom', paddingBottom: '5px', paddingTop: '30px' }}>
								<SignatureField style={{ borderBottom: '1px solid #000', minHeight: '60px', paddingBottom: '5px' }}>
									{image && <Image src={image} alt="signature" />}
								</SignatureField>
								<SignatureLabel style={{ fontSize: '10px', marginTop: '3px' }}>Applicant Signature</SignatureLabel>
							</td>
						</tr>
					</tbody>
				</IrelandSignatureTable>
			</SignatureSection>
		</IrelandAisContentWrapper>
	</>
);

// USA consent content
const usaConsentContent = (primaryName: PrimaryName, timeStamp: Date, image: string, workflow: Workflow) => (
	<>
		<AisHeaderWrapper>
			<AisHeader3>International Consent/Authorization</AisHeader3>
			<div style={{ fontSize: '12px', marginTop: '15px', fontWeight: 'bold' }}>Consent/Authorization</div>
		</AisHeaderWrapper>
		<AisContentWrapper>
			<p style={{ marginBottom: '15px' }}>
				In connection with this request I authorize all governmental and private agencies within the European Union, or
				any member state thereof, Asia Pacific (APAC); Middle East; South America or African regions, to release
				information they may have relating to my criminal record, educational or employment record to the person or
				company with which this form has been filed, or their agent for that purpose in which this consent was obtained.
				I authorize this consent to any nation outside the E.U or my home country of{' '}
				{workflow.countryCode ? getCountryName(workflow.countryCode) : '_____________________'}.
			</p>
			<p style={{ marginBottom: '15px' }}>
				I understand that data will not be transferred to any country that lies outside the European Economic Area
				(EEA); Asia Pacific (APAC); Middle East; South America or African regions; however data can be transferred with
				the consent of the data subject (applicant) at the point of collection where it is known that there will be a
				need or desire to transfer such data. Further, in accordance with the host nation laws regarding the release of
				information, the Fair Credit Reporting (FCRA), 15 U.S.C. 1681-1681u, Data Protection Privacy Act 1998, European
				Directive on Data Protection 95/46/EC and others, I authorize the release and transmittal of information from
				any country to any required agency that may have a legitimate business need, and to a company that holds this
				consent or any of its affiliate companies should the need arise. I further authorize any agent, to the extent
				required by any laws, rules applicable to any country, to provide the data pursuant to this authorization to
				governmental authorities in any country in connection with any regulatory filings it may submit to authorities.
			</p>
			<p style={{ marginBottom: '15px' }}>
				I understand and agree that my appointment is conditional upon the verification, to the Company&apos;s
				satisfaction, of the information provided on this form and that this information and that contained on attached
				documents, if required, is true and complete to the best of my knowledge.
			</p>
			<p style={{ marginBottom: '25px' }}>
				My signature below acknowledges the fact that I have read, understand and freely give my unambiguous consent to
				the handling of my personal information. Furthermore, I hereby release the aforesaid parties or the Company or
				individuals that release information about me from any liability whatsoever in collecting and disseminating the
				information obtained.
			</p>

			<SignatureSection>
				<USASignatureTable>
					<tbody>
						<tr>
							<td style={{ width: '60%', verticalAlign: 'bottom', paddingBottom: '5px' }}>
								<SignatureField style={{ borderBottom: '1px solid #000', minHeight: '30px', paddingBottom: '5px' }}>
									{primaryName.firstName} {primaryName.middleNames} {primaryName.lastName}
								</SignatureField>
								<SignatureLabel style={{ fontSize: '10px', marginTop: '3px' }}>
									Applicant Name (please print in black ink)
								</SignatureLabel>
							</td>
							<td style={{ width: '40%', verticalAlign: 'bottom', paddingBottom: '5px', paddingLeft: '20px' }}>
								<SignatureField style={{ borderBottom: '1px solid #000', minHeight: '30px', paddingBottom: '5px' }}>
									{safeFormat(timeStamp, 'MM/dd/yyyy')}
								</SignatureField>
								<SignatureLabel style={{ fontSize: '10px', marginTop: '3px' }}>Date (month/day/year)</SignatureLabel>
							</td>
						</tr>
						<tr>
							<td colSpan={2} style={{ verticalAlign: 'bottom', paddingBottom: '5px', paddingTop: '20px' }}>
								<SignatureField style={{ borderBottom: '1px solid #000', minHeight: '30px', paddingBottom: '5px' }}>
									{workflow.icrmDetails?.secondaryIdentificationNumber || ''}
								</SignatureField>
								<SignatureLabel style={{ fontSize: '10px', marginTop: '3px' }}>
									Government ID, PAN Card, or similar, if applicable
								</SignatureLabel>
							</td>
						</tr>
						<tr>
							<td colSpan={2} style={{ verticalAlign: 'bottom', paddingBottom: '5px', paddingTop: '20px' }}>
								<SignatureField style={{ borderBottom: '1px solid #000', minHeight: '60px', paddingBottom: '5px' }}>
									{image && <Image src={image} alt="signature" />}
								</SignatureField>
								<SignatureLabel style={{ fontSize: '10px', marginTop: '3px' }}>Applicant Signature</SignatureLabel>
							</td>
						</tr>
					</tbody>
				</USASignatureTable>
			</SignatureSection>
		</AisContentWrapper>
	</>
);

export const ConsentDocumentAISView = ({
	image,
	workflow,
	customerName,
	forceInternationalConsent = false,
	candidatePhone = '',
	currentUser,
	ownerUser
}: {
	image: string;
	workflow: Workflow;
	customerName?: string;
	forceInternationalConsent?: boolean;
	candidatePhone?: string;
	currentUser?: User;
	ownerUser?: User;
}) => {
	if (!workflow || !workflow.primaryName) {
		return null;
	}

	const criminalCheckCountry = workflow.icrmDetails?.criminalCheckCountry;
	const creditCheckCountry = workflow.icrdDetails?.creditCheckCountry;
	const checkCountry = criminalCheckCountry || creditCheckCountry;

	const getConsentContent = () => {
		// If forceInternationalConsent is true, always return the international consent
		if (forceInternationalConsent) {
			return aisInternationalConsentContent(workflow.primaryName, workflow?.consent?.signedTimestamp, image);
		}

		if (!hasValue(checkCountry)) {
			// If no country is specified, default to international consent
			return aisInternationalConsentContent(workflow.primaryName, workflow?.consent?.signedTimestamp, image);
		}

		// Priority order: Country-specific > Regional > EU > International
		// Country-specific consents
		if (checkCountry === 'BEL') {
			return euAuthorizationConsentContent(workflow, workflow?.consent?.signedTimestamp, image);
		}
		if (checkCountry === 'BRA') {
			return brazilConsentContent(
				workflow.primaryName,
				workflow?.consent?.signedTimestamp,
				image,
				workflow,
				customerName
			);
		}
		if (checkCountry === 'CAN') {
			return canadaConsentContent(
				workflow.primaryName,
				workflow?.consent?.signedTimestamp,
				image,
				workflow,
				candidatePhone,
				customerName,
				currentUser,
				ownerUser
			);
		}
		if (checkCountry === 'CHN') {
			return chinaConsentContent(workflow.primaryName, workflow?.consent?.signedTimestamp, image, workflow);
		}
		if (checkCountry === 'FIN') {
			return euAuthorizationConsentContent(workflow, workflow?.consent?.signedTimestamp, image);
		}
		if (checkCountry === 'FRA') {
			return franceConsentContent(workflow.primaryName, workflow?.consent?.signedTimestamp, image, workflow);
		}
		if (checkCountry === 'DEU') {
			return germanyConsentContent(
				workflow.primaryName,
				workflow?.consent?.signedTimestamp,
				image,
				workflow,
				customerName
			);
		}
		if (checkCountry === 'IRL') {
			return euInternationalConsentContent(workflow.primaryName, workflow?.consent?.signedTimestamp, image, workflow);
		}
		if (checkCountry === 'NLD') {
			return euInternationalConsentContent(workflow.primaryName, workflow?.consent?.signedTimestamp, image, workflow);
		}
		if (checkCountry === 'SAU') {
			return saudiArabiaConsentContent(workflow.primaryName, workflow?.consent?.signedTimestamp, image);
		}
		if (checkCountry === 'ZAF') {
			return southAfricaConsentContent(
				workflow.primaryName,
				workflow?.consent?.signedTimestamp,
				image,
				workflow,
				customerName
			);
		}
		if (checkCountry === 'USA') {
			return usaConsentContent(workflow.primaryName, workflow?.consent?.signedTimestamp, image, workflow);
		}

		// Regional consents
		if (isCentralAmericanCountry(checkCountry)) {
			return latinAmericanConsentContent(
				workflow.primaryName,
				workflow?.consent?.signedTimestamp,
				image,
				workflow,
				customerName
			);
		}
		if (isSouthAmericanCountry(checkCountry)) {
			return southAmericanConsentContent(
				workflow.primaryName,
				workflow?.consent?.signedTimestamp,
				image,
				workflow,
				customerName
			);
		}

		// EU consent (for non-specific EU countries)
		if (isEuCountry(checkCountry)) {
			return euConsentContent(workflow, workflow?.consent?.signedTimestamp, image);
		}

		// Default: International consent
		return aisInternationalConsentContent(workflow.primaryName, workflow?.consent?.signedTimestamp, image);
	};

	return <A4Page>{getConsentContent()}</A4Page>;
};

ConsentDocumentAISView.defaultProps = {
	customerName: undefined,
	forceInternationalConsent: false
};

const A4Page = styled.section`
	margin: 35px 50px 26px 50px;
	width: 635px;
	font-size: 12px;
`;
const Image = styled.img`
	width: 140px;
	height: 70px;
	padding: 10px 0;
`;

const Header2 = styled.h2`
	font-style: bold;
`;

const ContentWrapper = styled.div`
	padding: 10px 15px 0 20px;
	font-weight: bold;
`;

const HeaderWrapper = styled.div`
	text-align: left;
`;

const RightCell = styled.div`
	width: 200px;
	padding-right: 5x;
`;

const LeftCell = styled.div`
	padding-right: 5px;
`;

const Name = styled.div`
	display: flex;
	display: -webkit-flex;
	margin-bottom: 5px;
`;

const AisHeaderWrapper = styled.div`
	text-align: center;
	margin-bottom: 20px;

	> div:first-child {
		font-size: 8px;
		margin-bottom: 5px;
	}
`;

const AisHeader4 = styled.h4`
	font-size: 12px;
	font-weight: bold;
	margin: 10px 0 5px 0;
	align-self: left;
	text-align: left;
`;

const AisHeader3 = styled.h3`
	font-size: 14px;
	font-weight: bold;
	margin: 10px 0 5px 0;
	align-self: center;
	text-align: center;
	text-decoration: underline;
`;

const IrelandAisHeader3 = styled.h3`
	font-size: 16px;
	font-weight: bold;
	margin: 10px 0 5px 0;
	align-self: center;
	text-align: center;
	text-decoration: underline;
`;

const BrazilHeader = styled.h3`
	font-size: 14px;
	font-weight: normal;
	margin: 10px 0 5px 0;
	text-align: left;
	text-decoration: none;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
`;

const LatinAmericanHeader = styled.h3`
	font-size: 14px;
	font-weight: normal;
	margin: 10px 0 5px 0;
	text-align: center;
	text-decoration: none;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
`;

const LatinAmericanSubHeader = styled.div`
	font-size: 12px;
	font-weight: normal;
	margin-top: 10px;
	text-align: center;
	text-decoration: none;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
`;

const AisContentWrapper = styled.div`
	font-size: 11px;
	line-height: 1.4;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;

	a {
		color: #0066cc;
		text-decoration: none;
	}
`;

const IrelandAisContentWrapper = styled.div`
	font-size: 14px;
	line-height: 1.4;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;

	a {
		color: #0066cc;
		text-decoration: none;
	}
`;

const AisContentWrapper2 = styled.div`
	font-size: 11px;
	line-height: 1.4;

	p {
		font-style: italic;
	}

	a {
		color: #0066cc;
		text-decoration: none;
	}
`;

const SignatureSection = styled.div`
	margin-top: 20px;
`;

const SignatureTable = styled.table`
	width: 100%;
	border-collapse: collapse;

	td {
		vertical-align: top;
		padding-bottom: 20px;
	}
`;

const SignatureLabel = styled.div`
	font-size: 11px;
	margin-bottom: 5px;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
`;

const SignatureField = styled.div`
	min-height: 25px;
	padding: 5px 0;
	min-width: 200px;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
`;

const GreenText = styled.span`
	color: #008000;
	text-decoration: underline;
`;

const UnderlineText = styled.span`
	text-decoration: underline;
`;

const BoldText = styled.span`
	font-weight: bold;
`;

const RedFooterNote = styled.p`
	font-size: 10px;
	margin-top: 25px;
	color: #ff0000;
`;

const ABNContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	margin: 10px 0;
`;

const ABNFooter = styled.p`
	font-size: 10px;
	text-align: center;
	margin: 0;
`;

const BankingHeader = styled.h3`
	font-size: 14px;
	font-weight: bold;
	margin: 10px 0 5px 0;
	text-decoration: underline;
`;

const ABNContainer1 = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	margin: 10px 0;
`;

const RedText = styled.p`
	color: #ff0000;
	font-weight: bold;
	margin: 15px 0;
`;

const ChinaHeaderWrapper = styled.div`
	text-align: center;
	margin-bottom: 15px;
`;

const ChinaHeader = styled.h3`
	font-size: 14px;
	font-weight: bold;
	text-decoration: underline;
	margin: 5px 0;
`;

const ChinaTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	margin-top: 15px;
	border: 1px solid #000;

	tr {
		border: 1px solid #000;
	}
`;

const ChinaTableCell = styled.td`
	border: 1px solid #000;
	padding: 12px;
	vertical-align: top;
	width: 40%;
	font-size: 10px;
	line-height: 1.6;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
`;

const ChinaTableCellValue = styled.td`
	border: 1px solid #000;
	padding: 12px;
	vertical-align: top;
	width: 60%;
	font-size: 10px;
	line-height: 1.6;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
`;

const ChinaSignatureTable = styled.table`
	width: 100%;
	border: 1px solid #000;
	border-collapse: collapse;

	td {
		border: 1px solid #000;
		padding: 8px;
		vertical-align: middle;
		font-size: 10px;
		font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
	}
`;

const GermanyFormTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	margin-top: 10px;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
`;

const GermanyTableCell = styled.td`
	padding: 8px;
	font-size: 10px;
	vertical-align: top;
`;

// France-specific styled components
const FranceHeaderWrapper = styled.div`
	text-align: center;
	margin-bottom: 15px;
`;

const FranceHeader = styled.h2`
	font-size: 16px;
	font-weight: bold;
	text-decoration: underline;
	margin: 5px 0;
	text-align: center;
`;

const FranceSubHeader = styled.div`
	font-size: 12px;
	font-weight: bold;
	margin: 5px 0;
	text-align: center;
`;

const FranceSubHeaderEng = styled.div`
	font-size: 11px;
	font-style: italic;
	margin: 5px 0;
	text-align: center;
`;

const FranceInstructionsTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	font-size: 10px;
`;

const FranceInstructionsCell = styled.td`
	border: 1px solid #000;
	padding: 10px;
	vertical-align: top;
	width: 50%;
	font-size: 10px;
	line-height: 1.5;
`;

const FranceFormBox = styled.div`
	border: 1px solid #000;
	padding: 10px;
	margin: 10px 0;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
`;

const FranceFormHeader = styled.div`
	font-size: 10px;
	font-weight: bold;
	margin-bottom: 10px;
	text-align: center;
`;

const FranceFormRow = styled.div`
	display: flex;
	align-items: center;
	margin: 8px 0;
	font-size: 10px;
`;

const FranceFormLabel = styled.span`
	font-size: 10px;
	margin-right: 8px;
	white-space: nowrap;
`;

const FranceFormValue = styled.span`
	font-size: 10px;
	border-bottom: 1px solid #000;
	flex: 1;
	min-height: 18px;
	padding: 2px 5px;
`;

const USASignatureTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;

	td {
		vertical-align: top;
	}
`;

const IrelandSignatureTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;

	td {
		vertical-align: top;
	}
`;

// Canada-specific styled components
const CanadaTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	margin-bottom: 2px;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
	border: 1px solid #000;
`;

const CanadaSectionHeaderCell = styled.th`
	background-color: #d9e1f2;
	padding: 4px 6px;
	font-size: 9px;
	font-weight: bold;
	text-align: left;
	border: 1px solid #000;
`;

const CanadaFormCell = styled.td`
	padding: 5px 6px;
	font-size: 8px;
	border: 1px solid #000;
	vertical-align: top;
	line-height: 1.3;
`;

const CanadaHeader = styled.h2`
	font-size: 14px;
	font-weight: bold;
	text-align: center;
	margin: 10px 0 5px 0;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
`;

const CanadaSubHeader = styled.div`
	font-size: 12px;
	font-weight: bold;
	text-align: center;
	margin: 5px 0 15px 0;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
`;

const CanadaFormGrid = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
`;

const CanadaFormRow = styled.div`
	display: flex;
	gap: 15px;
	align-items: flex-start;
`;

const CanadaFormField = styled.div`
	display: flex;
	flex-direction: column;
`;

const CanadaLabel = styled.div`
	font-size: 9px;
	margin-bottom: 3px;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
`;

const CanadaValue = styled.div`
	border-bottom: 1px solid #000;
	min-height: 18px;
	padding: 2px 5px;
	font-size: 10px;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
`;

const CanadaDeclarationTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	margin-top: 15px;
	font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;

	td,
	th {
		border: 1px solid #000;
		padding: 8px;
		text-align: left;
	}
`;

const CanadaTableHeader = styled.th`
	font-size: 10px;
	font-weight: bold;
	background-color: #f0f0f0;
`;

const CanadaTableCell = styled.td`
	font-size: 10px;
	min-height: 30px;
`;
